import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ColumnDef,
  Row as TanRow,
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Boxes,
  ChevronDown,
  ChevronRight,
  EyeOff,
  HelpCircle,
  Info,
  RefreshCw,
  Settings2,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import * as api from "@/api/supply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { extractErrorMessage } from "@/lib/api-errors";
import { SUPPLY_UI } from "./features";
import { usePersistedState } from "@/lib/usePersistedState";
import { cn } from "@/lib/utils";
import { DistributionHidePicker } from "./DistributionHidePicker";
import type { HideChannel } from "./DistributionHidePicker";
import {
  dec1,
  depthFmt,
  fmtDate,
  fmtDateTime,
  int0,
  timeAgo,
  traceNum,
  traceStr,
  wbSourceLabel,
} from "./format";
import {
  fixationScope,
  hiddenCounts,
  hiddenWords,
  NOTHING_HIDDEN,
  parseHidden,
  stringifyHidden,
  visibleProducts,
} from "./hideMine";
import type { HiddenByMe } from "./hideMine";
import {
  ASSEMBLY_BLOCK,
  AssemblyBlockBadge,
  Cell,
  Hint,
  RegionMultiSelect,
} from "./shared";
import type { ColFilterKind, ColMeta } from "./table";
import {
  ColumnsMenu,
  FilterChips,
  HeaderCell,
  keepFilter,
  metaOf,
  passesFilter,
  visibleSelection,
} from "./table";
import { useColumnResize } from "./useColumnResize";
import { PaidStorageBadge } from "./PaidStorageBadge";
import { WbSourceBar } from "./WbSourceBar";
import { addPaid, sumPaid } from "./paidStorage";
import type { PaidAgg } from "./paidStorage";
import {
  cardInBoxMap,
  cardKey,
  failedDemandChannels,
  fillInBox,
  newProductSeeds,
  seededVerdict,
} from "./newProducts";

const KEYS = {
  wb: ["supply", "analytics", "wb"],
  oz: ["supply", "analytics", "ozon"],
  ym: ["supply", "analytics", "ym"],
  cap: ["supply", "analytics", "product-capacity"],
  dec: ["supply", "decisions"],
  alloc: ["supply", "analytics", "allocation"],
  legals: ["supply", "legals-map"],
  cards: ["supply", "channel-skus"],
  targets: ["supply", "targets"],
  params: ["supply", "params"],
};

// Движок распределения: режим = выкл | стратегия. Ярлыки причин + подсветка «почему».
type EngineMode = "off" | api.AllocStrategy;

const REASON_LABEL: Record<string, string> = {
  full: "закрыто полностью",
  deficit_pool: "не хватило пула товара",
  deficit_packaging: "упёрлись в мощность упаковки",
  no_need: "потребности нет",
  no_margin_residual: "остаточный канал (нет маржи)",
  loss_making: "убыточный — из остатка",
  box_remainder: "остаток < короба (квант)",
  no_base: "вне флаконной модели (доупак неприменим)",
  spec_problem: "спека не разворачивается — дефект данных 1С",
  no_pool: "нет пула: нет спеки канала / спеки спорят / нет моста 1С",
};
const REASON_TEXT: Record<string, string> = {
  full: "text-emerald-500",
  deficit_pool: "text-red-500",
  deficit_packaging: "text-amber-500",
  no_need: "text-muted-foreground/50",
  no_margin_residual: "text-slate-400 dark:text-slate-500",
  loss_making: "text-red-500",
  box_remainder: "text-amber-500",
  no_base: "text-slate-400 dark:text-slate-500",
  spec_problem: "text-red-500",
  no_pool: "text-slate-400 dark:text-slate-500",
};
// источник значения «Решение» → рамка ввода + бейдж (движок / сохранено вручную / устарело)
const DECISION_SRC: Record<
  string,
  { border: string; text: string; badge: string } | null
> = {
  engine: null, // рекомендация движка — дефолт, без выделения
  need: null, // движок выкл → потребность, без выделения
  zero: null,
  edited: { border: "border-amber-400", text: "text-amber-500", badge: "●" }, // не сохранено
  saved: {
    border: "border-blue-300 dark:border-blue-800",
    text: "text-blue-500",
    badge: "✎",
  }, // сохранено вручную
  saved_stale: {
    border: "border-orange-400",
    text: "text-orange-500",
    badge: "✎!",
  }, // сохранено, расходится с движком или с потребностью (товар уже в пути)
};
const FRS_STORAGE = "distGridFrsV2"; // ключ бампнут: ширины теперь по id колонки, а не по индексу
const VIS_STORAGE = "distGridCols";
const GROUP_STORAGE = "distGridGroup";
const SEL_STORAGE = "distGridSel";
/** Личное скрытие каналов/регионов («Скрыть у себя») — только этот браузер, только этот человек. */
const HIDE_STORAGE = "distGridHiddenMine";
const ROW_H = 28; // плотная строка: 12px кегль + инпут h-6
const clampDay = (n: number): number =>
  Math.max(1, Math.min(1000, Math.round(n) || 1));

// Ширины по id колонки (fr). По id, а не по позиции — колонки можно прятать и переставлять.
const DEFAULT_FRS: Record<string, number> = {
  article: 1,
  name: 2.6,
  orders: 0.85,
  stock: 0.85,
  supply: 0.75,
  days_avail: 0.8,
  speed: 0.85,
  depth: 0.85,
  future_depth: 1,
  ready: 0.85,
  assemble: 0.85,
  in_box: 0.7,
  need: 1,
  decision: 1.15,
  boxes: 0.9,
};
// «Допоставить» по умолчанию скрыт: он показан вторым числом внутри «Решения». Колонка остаётся,
// чтобы по сырой потребности можно было сортировать и фильтровать.
const DEFAULT_VISIBILITY = { need: false };

type Channel = "wb" | "ozon" | "ym";
const CHANNELS: Channel[] = ["wb", "ozon", "ym"];
const CH_LABEL: Record<Channel, string> = {
  wb: "Wildberries",
  ozon: "Ozon",
  ym: "Яндекс.Маркет",
};
const CH_BADGE: Record<Channel, string> = {
  wb: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  ozon: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  ym: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
};

interface Leaf {
  channel: Channel;
  region: string;
  orders: number;
  stock: number;
  supply: number;
  days_avail: number; // справочно: дней с остатком > 95-го перцентиля заказов (в скорость не входит)
  speed: number; // max(speed_7d, speed_30d) — с бэка
  speed_7d: number; // заказы за последние 7 дней / 7
  speed_30d: number; // заказы за 30 дней / 30
  depth: number;
  need: number; // Допоставить (округлён по коробам)
  in_box: number;
  disabled: boolean; // канал/регион выключен в настройках → потребность обнулена
  /** Ozon: платное хранение в кластере (справка, в потребность не входит) */
  paid?: PaidAgg;
}
interface ChGroup {
  channel: Channel;
  regions: Leaf[];
  orders: number;
  stock: number;
  supply: number;
  need: number;
}
interface Product {
  nom_key: string;
  article: string;
  name: string;
  channels: ChGroup[];
  ready: number; // готово (пул)
  assemble: number; // доупак (пул)
  orders: number;
  stock: number;
  supply: number;
  need: number;
  warnings: string[];
  block: string; // "" | "no_base" (вне флаконной модели) | "spec_problem" (дефект спеки 1С)
  /** «на вывод» в 1С: НЕ производим, но остатки возим и распродаём — иначе они лягут на складе */
  delisting: boolean;
  /** Каналы, где спецификация 1С говорит «возим», а мост sku не заполнен: канал достроить нечем.
   *  Показываем бейджем — иначе строка просто не раскрывается и объяснить это нечем. */
  noSku: Channel[];
  /** Ни заказов, ни остатка, ни поставок в пути ни на одной площадке: товара нет ни в одном отчёте
   *  спроса, он засеян из карточек 1С (`newProducts.ts`) — новинка или забытая карточка. */
  noMovement: boolean;
}

/** Регион-заглушка бэкенда для несопоставленных складов и округов (`UNKNOWN_REGION` там же). */
const UNKNOWN_REGION = "Не известен";

const decKey = (nom: string, ch: Channel, region: string): string =>
  `${nom}|${ch}|${region}`;
/** Ключ «канал|регион» — им же склеен составной уровень (`LEVELS`). */
const regNodeKey = (ch: Channel, region: string): string => `${ch}|${region}`;
/** Приоритет состояний сборки (тот же, что на бэке): поломка спеки важнее «вне флаконной модели». */
const BLOCK_RANK: Record<string, number> = {
  "": 0,
  no_base: 1,
  spec_problem: 2,
};
const worseBlock = (a: string, b: string): string =>
  (BLOCK_RANK[b] ?? 0) > (BLOCK_RANK[a] ?? 0) ? b : a;

/** Глубина при нулевой скорости: «хватит навсегда». Одно число на все шесть мест, где раньше
 *  стояло голое 9999. */
const DEPTH_INF = 9999;

/** На сколько дней хватит ПОСЛЕ поставки: (остаток + в пути + решение) / скорость.
 *  Ровно то, что проверяет гард перетарки на бэке; менеджеру нужен результат решения, а не
 *  только текущее состояние. Одна формула на все уровни — скорость аддитивна по регионам. */
const futureDepth = (
  stock: number,
  supply: number,
  decision: number,
  speed: number,
): number => (speed > 0 ? (stock + supply + decision) / speed : DEPTH_INF);

/** Числа строки — считаются ОДИН раз при построении дерева и служат и фильтрам, и сортировке,
 *  и ячейкам. `decision` здесь — «зафиксированная» картина (без несохранённых правок): иначе
 *  каждое нажатие клавиши пересобирало бы дерево и строки прыгали бы под курсором. */
type RowValues = Record<string, number>;

const leafValues = (l: Leaf, dec: number): RowValues => ({
  orders: l.orders,
  stock: l.stock,
  supply: l.supply,
  days_avail: l.days_avail,
  speed: l.speed,
  depth: l.depth,
  in_box: l.in_box,
  need: l.need,
  decision: dec,
  future_depth: futureDepth(l.stock, l.supply, dec, l.speed),
  boxes: l.in_box > 0 ? dec / l.in_box : 0,
});

/** Агрегат уровня: суммы по детям + производные от СУММ (скорость аддитивна по регионам).
 *
 *  Считается ТОЛЬКО по целям, куда планируем.
 *
 *  ⚠️ Выключенная цель не даёт узлу ничего: ни фактов, ни плана. Планирование идёт по конкретным
 *  кластерам, и «заказы 1320» при шести показанных регионах на 339 — это не правда о товаре, а
 *  число, которое невозможно проверить глазами (владелец, 14.09.2026). Скрытие выключенных
 *  (`showOff`) на итог по-прежнему не влияет: они не считаются ни в показанном виде, ни в скрытом.
 */
const aggValues = (children: { v: RowValues; l?: Leaf }[]): RowValues => {
  const live = children.filter((c) => !c.l?.disabled);
  const sum = (k: string): number =>
    live.reduce((a, c) => a + (c.v[k] ?? 0), 0);
  const [stock, supply, speed] = [sum("stock"), sum("supply"), sum("speed")];
  const dec = sum("decision"); // выключенным решение уже обнулил `decisionBase` — гард тут был бы третьим
  return {
    orders: sum("orders"),
    stock,
    supply,
    speed,
    need: sum("need"),
    decision: dec,
    days_avail: 0,
    in_box: 0,
    boxes: 0,
    depth: speed > 0 ? stock / speed : DEPTH_INF,
    future_depth: futureDepth(stock, supply, dec, speed),
  };
};

/** Колонки, чей фильтр отсекает ТОВАР целиком (остальные — отдельные регионы). */
const PRODUCT_VALUE: Record<
  string,
  { kind: ColFilterKind; get: (p: Product) => unknown }
> = {
  article: { kind: "list", get: (p) => p.article },
  // «Товар» на листьях показывает НАЗВАНИЕ РЕГИОНА, поэтому фильтр обязан искать и по ним —
  // иначе набранное имя региона не находило бы ничего в колонке, которая их же и показывает
  name: {
    kind: "text",
    get: (p) =>
      `${p.name} ${p.channels.flatMap((c) => c.regions.map((r) => r.region)).join(" ")}`,
  },
  ready: { kind: "number", get: (p) => p.ready },
  assemble: { kind: "number", get: (p) => p.assemble },
};
const LEAF_VALUE = leafValues({} as Leaf, 0); // набор id листовых колонок (значения не важны)
/** Запись плюс уже посчитанные числа строки. */
type Rec = RecKey & { v: RowValues };

/** Режим группировки. Лист всегда одна и та же тройка (товар × канал × регион) — меняется только
 *  порядок вложенности, поэтому режим не трогает ни числа, ни фильтры, ни фиксацию. */
type GroupMode = "prod-ch-reg" | "prod-reg" | "reg-prod";
const GROUP_MODES: [GroupMode, string][] = [
  ["prod-ch-reg", "Товар → МП → Регион"],
  ["prod-reg", "Товар → Канал: Регион"],
  ["reg-prod", "Канал: Регион → Товар"],
];

/** Что за сущность в строке. `leaf` — не измерение, а РОЛЬ: строка, у которой есть конкретный
 *  регион одного товара, то есть та, где редактируется «Решение». В разных режимах листом
 *  оказывается разное измерение. */
type Dim = "product" | "channel" | "chregion" | "region";

/** Запись, из которой растёт дерево: конкретный (товар × канал × регион). */
interface RecKey {
  p: Product;
  ch: Channel;
  l: Leaf;
}
/** Уровень дерева: чем группируем и как называется в кнопке «раскрыть до». */
interface Level {
  dim: Dim;
  label: string;
  keyOf: (r: RecKey) => string;
}
const joinKey = (prefix: string, part: string): string =>
  prefix ? `${prefix}|${part}` : part;
/** Что уровень ФИКСИРУЕТ в строке. Узел несёт то, что определил его путь от корня: в
 *  «Канал: Регион → Товар» строка товара лежит внутри региона и знает и канал, и регион, а в
 *  «Товар → МП → Регион» — не знает ни того, ни другого. Лишнее поле не безобидно: `p` на строке
 *  региона заставил бы сортировку по «Артикулу» упорядочивать группы по случайному товару внутри. */
const fixedBy = (dim: Dim, r: RecKey): Partial<TRow> =>
  dim === "product"
    ? { p: r.p }
    : dim === "channel"
      ? { chCode: r.ch }
      : dim === "chregion"
        ? { chCode: r.ch, region: r.l.region }
        : { region: r.l.region };

/** Режимы группировки — ДАННЫЕ, а не три ветки кода. Ключ узла = ключи уровней от корня, склеенные
 *  `joinKey`, поэтому дерево, раскрытие и дельта несохранённых правок пользуются ОДНИМИ И ТЕМИ ЖЕ
 *  строками. Пока их собирали тремя независимыми шаблонами, рассинхрон не давал ни ошибки типа,
 *  ни симптома — кнопка раскрытия просто молча не работала. Последний уровень всегда лист: уровни
 *  вместе покрывают всю тройку, поэтому в его группе ровно одна запись. */
const LEVELS: Record<GroupMode, Level[]> = {
  "prod-ch-reg": [
    { dim: "product", label: "товара", keyOf: (r) => r.p.nom_key },
    { dim: "channel", label: "маркетплейса", keyOf: (r) => r.ch },
    { dim: "region", label: "региона", keyOf: (r) => r.l.region },
  ],
  // канал схлопнут в бейдж на строке региона: два уровня вместо трёх
  "prod-reg": [
    { dim: "product", label: "товара", keyOf: (r) => r.p.nom_key },
    {
      dim: "chregion",
      label: "региона",
      keyOf: (r) => regNodeKey(r.ch, r.l.region),
    },
  ],
  // регион (с бейджем канала) наверху, товары внутри
  "reg-prod": [
    {
      dim: "chregion",
      label: "региона",
      keyOf: (r) => regNodeKey(r.ch, r.l.region),
    },
    { dim: "product", label: "товара", keyOf: (r) => r.p.nom_key },
  ],
};
/** Ключи узлов-предков записи, от корня. Один список строит и дерево, и раскрытие, и дельту. */
const pathKeys = (mode: GroupMode, r: RecKey): string[] => {
  const out: string[] = [];
  let key = "";
  for (const lv of LEVELS[mode]) {
    key = joinKey(key, lv.keyOf(r));
    out.push(key);
  }
  return out;
};

interface TRow {
  dim: Dim;
  key: string;
  v: RowValues;
  subRows: TRow[];
  p?: Product; // есть у узла-товара и у любого листа
  chCode?: Channel; // есть у узла канала, составного узла и листа
  region?: string; // есть у составного узла и листа
  nKids?: number; // видимых детей: у нераскрытого узла subRows пусты, а знать надо
  off?: number; // сколько регионов скрыто как выключенные
  l?: Leaf; // ТОЛЬКО у листа: наличие = строку можно редактировать
  /** Ozon: платное хранение — у листа его ячейка, у узла с фиксированным регионом (группа
   *  кластера) сумма по входящим (кластер × SKU). У узлов без региона нет: там это не кластер. */
  paid?: PaidAgg;
}
const blockInfo = (p: Product) =>
  p.block ? ASSEMBLY_BLOCK[p.block] : undefined;

/** Перечень регионов канала для меню и фильтров — из уже посчитанных целей, а не заново из строк. */
const regionsFromTargets = (
  t: Record<Channel, string[]>,
): Record<Channel, string[]> => t;

/** Настройки «куда планируем»: тумблеры каналов и регионов. `memo` обязателен: без него вынос в
 *  компонент ничего не даёт — родитель перерисовывается на каждый кадр скролла, а с ним и ~60
 *  чекбоксов региона. Все пропсы стабильны по ссылке. */
const PlanningTargets = React.memo(function PlanningTargets({
  regions,
  off,
  isOff,
  onToggle,
  busy,
  showOff,
  onShowOff,
}: {
  regions: Record<Channel, string[]>;
  off: Set<string>;
  isOff: (mp: string, region?: string) => boolean;
  onToggle: (v: {
    mp_code: string;
    region?: string;
    regions?: string[];
    enabled: boolean;
  }) => void;
  busy: boolean;
  showOff: boolean;
  onShowOff: (v: boolean) => void;
}): React.ReactElement {
  // Настройка ЛИЧНАЯ (владелец, 17.09.2026): выключенное пропадает из потребности и фиксации
  // ТОЛЬКО у этого пользователя. Поэтому ни подтверждения, ни предупреждения на переключении —
  // цена клика не выходит за пределы своего экрана.
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          title="Куда планируете ВЫ: выключенное не попадает в вашу потребность и фиксацию; у других пользователей — своё. Спрятать строки с экрана, не трогая расчёт, — «Скрыть у себя»"
        >
          <Settings2 className="size-4" />
          Куда планируем
          {off.size > 0 && (
            <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              выкл {off.size}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[70vh] w-96 overflow-auto p-3 text-sm"
      >
        <p className="mb-2 text-xs text-muted-foreground">
          <b>Куда планируете вы:</b> выключенное не попадает в вашу потребность
          и фиксацию; у других пользователей — своё. Чтобы убрать строки с
          экрана, не меняя расчёт, — <b>«Скрыть у себя»</b>.
        </p>
        <p className="mb-2 text-xs text-muted-foreground">
          В таблице выключенные регионы по умолчанию скрыты, а на строке канала
          стоит счётчик «N выкл.». Пересчёт спроса — сразу.
        </p>
        <label className="mb-2 flex items-center gap-2 rounded border px-2 py-1 text-xs">
          <input
            type="checkbox"
            checked={showOff}
            onChange={(e) => onShowOff(e.target.checked)}
          />
          показывать выключенные в таблице
        </label>
        {CHANNELS.map((c) => {
          const chanOff = isOff(c);
          const offCount = regions[c].filter((r) => isOff(c, r)).length;
          return (
            <div key={c} className="mb-3 last:mb-0">
              <label className="flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={!chanOff}
                  disabled={busy}
                  onChange={(e) =>
                    onToggle({ mp_code: c, enabled: e.target.checked })
                  }
                />
                <span
                  className={cn("rounded px-1.5 py-0.5 text-xs", CH_BADGE[c])}
                >
                  {CH_LABEL[c]}
                </span>
                {chanOff && (
                  <span className="text-xs text-muted-foreground">
                    канал выключен целиком
                  </span>
                )}
              </label>
              <div
                className={cn(
                  "mt-1 grid gap-0.5 pl-6",
                  chanOff && "opacity-40",
                )}
              >
                {/* массовое действие: у Ozon под тридцать кластеров, по одному их не наотмечаешься */}
                <label className="flex items-center gap-2 border-b pb-1 text-xs font-medium">
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          offCount > 0 && offCount < regions[c].length;
                    }}
                    checked={offCount === 0}
                    // у канала без регионов в снимке массовое действие пустое: бэкенд такой запрос
                    // отвергает (пустой список = потерянное действие), поэтому не даём его послать
                    disabled={chanOff || busy || regions[c].length === 0}
                    onChange={() =>
                      onToggle({
                        mp_code: c,
                        regions: regions[c],
                        enabled: offCount > 0,
                      })
                    }
                  />
                  все регионы
                  {offCount > 0 && (
                    <span className="font-normal text-muted-foreground">
                      · выключено {offCount}
                    </span>
                  )}
                </label>
                {regions[c].map((r) => (
                  <label key={r} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!isOff(c, r)}
                      disabled={chanOff || busy}
                      onChange={(e) =>
                        onToggle({
                          mp_code: c,
                          region: r,
                          enabled: e.target.checked,
                        })
                      }
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
});

/** Группа товаров, у которых «Доупак» недоступен: счётчик + раскрытие + список артикулов. */
function BlockedGroup({
  items,
  summary,
  tail,
  note,
  danger,
}: {
  items: Product[];
  summary: string;
  tail: string;
  note: string;
  danger?: boolean;
}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <details
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        danger
          ? "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
          : "bg-card",
      )}
    >
      <summary
        className={cn(
          "cursor-pointer select-none",
          danger ? "text-red-700 dark:text-red-300" : "text-muted-foreground",
        )}
      >
        {summary}:{" "}
        <b className={cn(!danger && "text-foreground")}>{items.length}</b>{" "}
        {items.length === 1 ? "товар" : "товаров"} — {tail}
      </summary>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((p) => (
          <span
            key={p.nom_key}
            className={cn(
              "rounded border px-1.5 py-0.5 text-xs",
              danger && "border-red-300 dark:border-red-900",
            )}
            title={p.warnings.join("; ") || p.name}
          >
            <span className="font-mono text-muted-foreground">
              {p.article || "—"}
            </span>{" "}
            <span className="text-foreground">{p.name.slice(0, 46)}</span>
          </span>
        ))}
      </div>
      <p
        className={cn(
          "mt-2 text-xs",
          danger
            ? "text-red-700/80 dark:text-red-300/80"
            : "text-muted-foreground",
        )}
      >
        {note}
      </p>
    </details>
  );
}

/** Аддитивные суммы по набору листов-регионов — ТОЛЬКО по тем, куда планируем.
 *
 *  ⚠️ Выключенная цель не даёт итогу НИЧЕГО: ни «Допоставить», ни заказов, ни остатка, ни «в пути».
 *  Раньше факты выключенных в сумму входили («это правда о товаре»), и строка товара показывала
 *  числа, которых не было ни в одной видимой строке — например заказы 1320 при шести показанных
 *  регионах на 339. Планируем мы по определённым кластерам, и итог обязан отвечать на вопрос
 *  «сколько у нас ТАМ, куда мы возим» (владелец, 14.09.2026). Сама строка выключенной цели свои
 *  числа сохраняет — видно, чего стоит запрет.
 */
function sumLeaves(leaves: Leaf[]): {
  orders: number;
  stock: number;
  supply: number;
  need: number;
} {
  let orders = 0,
    stock = 0,
    supply = 0,
    need = 0;
  for (const l of leaves) {
    if (l.disabled) continue;
    orders += l.orders;
    stock += l.stock;
    supply += l.supply;
    need += l.need;
  }
  return { orders, stock, supply, need };
}

type DecisionSource =
  | "edited"
  | "saved"
  | "saved_stale"
  | "engine"
  | "need"
  | "zero";

/** Всё, что нужно ячейке «Решение». Отдельным интерфейсом, чтобы колонки оставались стабильными:
 *  ячейка читает свежие колбэки через ref-значение, а не пересобирает определение таблицы. */
interface DecisionCtx {
  decisionOf: (nom: string, ch: Channel, l: Leaf) => number;
  decisionSourceOf: (nom: string, ch: Channel, l: Leaf) => DecisionSource;
  recOf: (
    nom: string,
    ch: Channel,
    region: string,
  ) => api.AllocationLine | undefined;
  allocByNom: Map<string, api.AllocationLine[]>;
  liveDecision: (r: TRow) => number; // «Решение» с учётом несохранённых правок (лист и агрегат)
  hasCap: boolean;
  engineReady: boolean;
  /** делим только физический остаток ГП: доупак в потолок не входит */
  shelf: boolean;
  saved: Map<string, number>;
  dropEdited: (key: string, onlyIfEquals?: number) => void;
  dropDecisionMut: {
    mutate: (v: { nom_key: string; channel: string; region: string }) => void;
  };
  setLeafDecision: (
    nom: string,
    ch: Channel,
    region: string,
    qty: number,
  ) => void;
  saveLeaf: (nom: string, ch: Channel, region: string) => void;
}

/** «Допоставить» и «Решение» слиты в одну колонку: главное число — что реально уедет, под ним
 *  серым — что было предложено (движок, а при выключенном движке — сырая потребность). Показываем
 *  предложение ТОЛЬКО когда оно расходится с решением: совпадение — не информация, а шум. */
/** «Решение» в строке ИТОГО: число плюс из чего оно сложилось.
 *
 *  ⚠️ Выключенные цели дают ноль (см. `decisionOf`), то есть в итог входит РОВНО то, куда мы
 *  сейчас планируем. Но проверить это по одной цифре было нельзя, и она читалась как «сюда
 *  попали чужие каналы». Поэтому канал и источник числа названы прямо: подставленная
 *  потребность — это ещё не решение менеджера. */
function TotalDecision({
  totals,
}: {
  totals: {
    decision: number;
    saved: number;
    byChannel: Record<string, number>;
  };
}): React.ReactElement {
  const live = (["wb", "ozon", "ym"] as const).filter(
    (c) => (totals.byChannel[c] ?? 0) > 0,
  );
  const proposed = totals.decision - totals.saved;
  return (
    <span
      className="flex items-center justify-end gap-1.5"
      title={
        `Только включённые вами цели: выключенный канал или кластер даёт ноль.\n` +
        live
          .map((c) => `${CH_LABEL[c]}: ${int0(totals.byChannel[c] ?? 0)}`)
          .join(" · ") +
        (proposed > 0
          ? `\nИз них ${int0(totals.saved)} решено (сохранено или движком), ` +
            `${int0(proposed)} подставлено из «Допоставить»`
          : "")
      }
    >
      {live.length === 1 && (
        <span className="text-[11px] text-muted-foreground">
          {CH_LABEL[live[0]!]}
        </span>
      )}
      {int0(totals.decision)}
    </span>
  );
}

function DecisionCell({
  row,
  ctx,
}: {
  row: TRow;
  ctx: DecisionCtx;
}): React.ReactElement {
  const proposalHint =
    "Предложено изначально: рекомендация движка, а при выключенном движке — расчётная потребность «Допоставить»";
  if (!row.l) {
    // агрегатная строка: своё «Решение» — сумма детей, число берём из уже посчитанных значений
    const isProduct = row.dim === "product" && row.p;
    const val = ctx.liveDecision(row);
    const need = row.v.need ?? 0;
    // в режиме полки потолок — только физический остаток ГП: доупака в этом режиме нет вовсе,
    // и «пул» из него сделал бы красную подсветку слепой ровно там, где она нужна
    const pool = isProduct
      ? row.p!.ready + (ctx.shelf ? 0 : row.p!.assemble)
      : 0;
    const deficit =
      Boolean(isProduct) && ctx.engineReady && ctx.hasCap && val > pool;
    return (
      <span className="flex items-center justify-end gap-1.5">
        {Math.round(need) !== val && (
          <span
            className="text-[11px] text-muted-foreground"
            title={proposalHint}
          >
            {int0(need)}
          </span>
        )}
        <span
          className={cn(deficit && "text-red-600 dark:text-red-400")}
          title={
            isProduct && ctx.hasCap && SUPPLY_UI.assembly
              ? `пул ${int0(pool)} · остаток ${int0(pool - val)}`
              : undefined
          }
        >
          {int0(val)}
          {deficit && " ⚠"}
        </span>
      </span>
    );
  }

  const p = row.p!;
  const ch = row.chCode!;
  const l = row.l;
  const val = ctx.decisionOf(p.nom_key, ch, l);
  const rec = ctx.recOf(p.nom_key, ch, l.region);
  const src = ctx.decisionSourceOf(p.nom_key, ch, l);
  const srcMeta = DECISION_SRC[src];
  const key = decKey(p.nom_key, ch, l.region);
  const proposal = ctx.engineReady && rec ? rec.allocated : Math.round(l.need);
  return (
    <div className="flex items-center gap-1">
      {proposal !== val && (
        <span
          className="shrink-0 text-[11px] text-muted-foreground"
          title={proposalHint}
        >
          {int0(proposal)}
        </span>
      )}
      <input
        type="number"
        min={0}
        value={val}
        disabled={l.disabled}
        onChange={(e) =>
          ctx.setLeafDecision(
            p.nom_key,
            ch,
            l.region,
            Number(e.target.value) || 0,
          )
        }
        onBlur={() => ctx.saveLeaf(p.nom_key, ch, l.region)}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "h-6 w-full min-w-0 rounded border bg-background px-1 text-right text-xs tabular-nums focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60",
          srcMeta ? srcMeta.border : "border-input",
        )}
      />
      {srcMeta && (
        <span
          className={cn(
            "shrink-0 text-[10px] font-medium leading-none",
            srcMeta.text,
          )}
          title={
            src === "saved_stale"
              ? rec
                ? `Сохранено вручную: ${int0(val)}. Движок сейчас: ${int0(rec.allocated)} — значение может быть устаревшим.`
                : `Сохранено вручную: ${int0(val)}, а «Допоставить» сейчас ${int0(Math.round(l.need))}: ` +
                  `столько уже едет или лежит на складе. Похоже, решение осталось от прошлой фиксации — ` +
                  `увезти его второй раз значит завезти лишнее.`
              : src === "saved"
                ? `Сохранено вручную: ${int0(val)}`
                : src === "edited"
                  ? "Изменено, не сохранено"
                  : "Рекомендация движка"
          }
        >
          {srcMeta.badge}
        </span>
      )}
      {(ctx.saved.has(key) || src === "edited") && (
        <button
          type="button"
          title="Сбросить решение: вернуться к рекомендации движка / потребности"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            ctx.dropEdited(key);
            // сохранённое удаляем всегда, когда оно есть: локальная правка могла быть уже
            // сохранена, и снять её одним лишь `edited` не получится
            if (ctx.saved.has(key))
              ctx.dropDecisionMut.mutate({
                nom_key: p.nom_key,
                channel: ch,
                region: l.region,
              });
          }}
        >
          <X className="size-3" />
        </button>
      )}
      {rec && (
        <WhyPopover
          line={rec}
          siblings={ctx.allocByNom.get(rec.nom_key) ?? [rec]}
        >
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            title={`движок: ${int0(rec.allocated)} · ${REASON_LABEL[rec.reason] ?? rec.reason}`}
            className="shrink-0 hover:opacity-70"
          >
            <HelpCircle
              className={cn(
                "size-3.5",
                REASON_TEXT[rec.reason] ?? "text-muted-foreground",
              )}
            />
          </button>
        </WhyPopover>
      )}
    </div>
  );
}

/** Виртуализированное тело таблицы. Вынесено из страницы намеренно: `useVirtualizer` перерисовывает
 *  СВОЕГО владельца на каждом кадре скролла, и пока он жил в странице, вместе с ним заново
 *  исполнялись шапка с 15 поповерами фильтров, строка ИТОГО и все панели управления. Здесь же
 *  ре-рендер задевает только строки. `memo` НЕ ставим: ячейки «Решения» должны обновляться при
 *  вводе, а он меняет состояние страницы, а не пропсы этого компонента. */
function TableBody({
  rows,
  gridTemplate,
}: {
  rows: TanRow<TRow>[];
  gridTemplate: string;
}): React.ReactElement {
  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    // окно отрисовки держим в СТРОКАХ: при переходе на плотную строку (34→28 px) прежние 12
    // давали на треть больше отрисованных ячеек, а тело намеренно не мемоизировано
    overscan: 8,
  });
  return (
    <div ref={parentRef} className="max-h-[68vh] overflow-auto">
      <div style={{ height: virt.getTotalSize(), position: "relative" }}>
        {virt.getVirtualItems().map((vi) => {
          const row = rows[vi.index];
          if (!row) return null;
          const r = row.original;
          const leaf = Boolean(r.l); // лист = редактируемая строка, в разных режимах это разный уровень
          const top = row.depth === 0;
          return (
            <div
              key={row.id}
              className={cn(
                "absolute left-0 top-0 grid w-full items-center border-b text-xs transition-colors",
                !leaf &&
                  top &&
                  "cursor-pointer bg-accent/30 font-medium hover:bg-accent/60",
                !leaf && !top && "cursor-pointer bg-muted/20 hover:bg-muted/50",
                leaf && "hover:bg-accent/60",
                leaf && r.l!.disabled && "opacity-50", // выключено — видно, но не планируется
              )}
              style={{
                height: ROW_H,
                transform: `translateY(${vi.start}px)`,
                gridTemplateColumns: gridTemplate,
              }}
              onClick={
                row.getCanExpand() ? row.getToggleExpandedHandler() : undefined
              }
            >
              {row.getVisibleCells().map((cell) => (
                <Cell key={cell.id} text={metaOf(cell.column).left}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Cell>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Настройки расчёта — ЗА ПОЛЬЗОВАТЕЛЕМ (`supply_user_params`), а не в состоянии страницы: раньше
 *  ГТЗ возвращался к дефолту на каждом F5, и человек считал поставку совсем не тем горизонтом,
 *  который сам себе выставил час назад. Тело страницы монтируется ТОЛЬКО когда настройки известны:
 *  спрос считается минуту, и посчитать его сперва дефолтными отсечками, а потом ещё раз своими —
 *  это две минуты и промежуточные числа, которых никто не просил. Дефолты присылает бэк; своей
 *  копии тут нет, иначе «ГТЗ теперь 30» пришлось бы менять с двух сторон. */
export function SupplyDistributionPage({
  onFixed,
}: { onFixed?: () => void | Promise<void> } = {}): React.ReactElement {
  const prefs = useQuery({
    queryKey: KEYS.params,
    queryFn: api.getSupplyParams,
    staleTime: Infinity,
  });
  if (prefs.error)
    return (
      <p className="text-sm text-destructive">
        {extractErrorMessage(
          prefs.error,
          "Не удалось загрузить настройки расчёта",
        )}
      </p>
    );
  if (!prefs.data)
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" /> Загружаю настройки
        расчёта…
      </p>
    );
  return <DistributionBody onFixed={onFixed} prefs={prefs.data} />;
}

function DistributionBody({
  onFixed,
  prefs,
}: {
  onFixed?: () => void | Promise<void>;
  prefs: api.SupplyParams;
}): React.ReactElement {
  const qc = useQueryClient();

  // «Зафиксировать потребность»: ЭФФЕКТИВНЫЕ «Решения» (движок + правки) → пачка поставок (вкладка «Отгрузки»)
  const fixMut = useMutation({
    mutationFn: (v: { lines: api.FixLine[]; note?: string }) =>
      api.fixDistribution(v.lines, v.note),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ["supply", "shipment-runs"] });
      qc.invalidateQueries({ queryKey: ["supply", "analytics"] }); // бронь могла уменьшить пул
      // про бронь тост молчит: её статус живёт в самом run'е и виден в «Отгрузках» — а когда бронь
      // выключена, хвост «бронь не создана» пугал после КАЖДОЙ фиксации
      toast.success(`Зафиксировано: ${run.shipments.length} поставок`);
      void onFixed?.();
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось зафиксировать")),
  });

  // Настройки расчёта живут ЗА ПОЛЬЗОВАТЕЛЕМ (таблица `supply_user_params`), а не в состоянии
  // страницы: раньше ГТЗ возвращался к дефолту на каждом F5, и человек считал поставку совсем не
  // тем горизонтом, который сам себе выставил час назад. Дефолты присылает бэк — своей копии тут
  // нет, иначе «ГТЗ теперь 30» пришлось бы менять с двух сторон.
  const prefsMut = useMutation({
    mutationFn: api.saveSupplyParams,
    onSuccess: (p) => qc.setQueryData(KEYS.params, p),
    onError: (e) =>
      toast.error(
        extractErrorMessage(e, "Не удалось сохранить настройки расчёта"),
      ),
  });

  // редактируемые отсечки calc_supply (ГТЗ/порог/потолок): draft — в инпутах, params — применённые
  // (с дебаунсом 800мс), они в ключе запросов → смена цифр перезапрашивает спрос и всё пересчитывается.
  const initial: api.CalcParams = {
    target: prefs.target,
    catalog: prefs.catalog,
    overstock: prefs.overstock,
  };
  const [draft, setDraft] = useState<api.CalcParams>(initial);
  const [params, setParams] = useState<api.CalcParams>(initial);
  useEffect(() => {
    const t = setTimeout(() => {
      setParams({
        target: clampDay(draft.target),
        catalog: clampDay(draft.catalog),
        overstock: clampDay(draft.overstock),
      });
    }, 800);
    return () => clearTimeout(t);
  }, [draft]);
  const pendingParams =
    clampDay(draft.target) !== params.target ||
    clampDay(draft.catalog) !== params.catalog ||
    clampDay(draft.overstock) !== params.overstock;

  const wb = useQuery({
    queryKey: [...KEYS.wb, params],
    queryFn: () => api.getWbAnalytics(params),
    staleTime: 5 * 60_000,
  });
  // ⚠️ Упал ПЕРЕзапрос — react-query держит прежние `data` рядом с `error`. Строить по ним таблицу,
  // подпись источника и фиксацию WB нельзя: это отчёт по прежним отсечкам / прежнему источнику.
  // При ошибке WB-данных нет вовсе (ошибка — в плашке и строке ошибок), Ozon/YM — как были.
  const wbData = wb.error ? undefined : wb.data;
  const oz = useQuery({
    queryKey: [...KEYS.oz, params],
    queryFn: () => api.getOzonAnalytics(params),
    staleTime: 5 * 60_000,
  });
  const ym = useQuery({
    queryKey: [...KEYS.ym, params],
    queryFn: () => api.getYmAnalytics(params),
    staleTime: 5 * 60_000,
  });
  // Остаток ГП + доупак. Запрос живой (`fetch_stock` по складу готовой продукции), поэтому нужен
  // всегда: «Остаток ГП» — это физически лежащий на складе товар, а не часть флаконной модели.
  // Снимок 1С требуется только «Доупаку» (ATB), сам остаток от него не зависит.
  const cap = useQuery({
    queryKey: [...KEYS.cap, SUPPLY_UI.assembly],
    queryFn: () => api.getProductCapacity(SUPPLY_UI.assembly),
    staleTime: 10 * 60_000,
  });
  const dec = useQuery({
    queryKey: KEYS.dec,
    queryFn: api.getDecisions,
    staleTime: 60_000,
  });
  // Карточки МП по 1С: где у товара ЕСТЬ карточка, даже если движения по ней ещё не было. Отчёты
  // спроса такие позиции не показывают вовсе, и завезти товар «куда ещё ни разу не возили» было
  // нечем. Держим долго: заводят карточки редко, а холодный ответ стоит ~30 с в 1С.
  const cards = useQuery({
    queryKey: KEYS.cards,
    queryFn: api.getChannelSkus,
    staleTime: 30 * 60_000,
  });
  // ⚠️ Пустой `mp_sku` в ответе — это НЕ «нет такой пары», а «в спецификации канал есть, а
  // реквизит в 1С не заполнен». Канал по такой паре не достроить, но и молчать нельзя: строка
  // просто не раскрывалась бы, и отличить «карточки нет» от «забыли заполнить» было бы невозможно.
  const cardSku = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of cards.data ?? [])
      if (r.mp_sku) m.set(`${r.channel}\u0000${r.nom_key}`, r.mp_sku);
    return m;
  }, [cards.data]);
  const cardNoSku = useMemo(() => {
    const m = new Set<string>();
    for (const r of cards.data ?? [])
      if (!r.mp_sku) m.add(`${r.channel}\u0000${r.nom_key}`);
    return m;
  }, [cards.data]);
  // Квант со спеки канала — для достроенного канала, у которого нет строки-соседа с квантом
  // (новинка без движения): иначе «Коробов» на её строках был бы прочерком.
  const cardInBox = useMemo(() => cardInBoxMap(cards.data ?? []), [cards.data]);
  // Отчёт спроса не загрузился → товары без движения не сеем (см. `failedDemandChannels`)
  const failedDemand = failedDemandChannels([
    { label: "WB", loaded: !!wbData, failed: !!wb.error },
    { label: "Ozon", loaded: !!oz.data, failed: !!oz.error },
    { label: "YM", loaded: !!ym.data, failed: !!ym.error },
  ]);
  const targets = useQuery({
    queryKey: KEYS.targets,
    queryFn: api.getDisabledTargets,
    staleTime: 60_000,
  });
  // выключенные цели ключуем ТОЧНО как строку БД: region "" = канал целиком, отдельного
  // множества для каналов не нужно (иначе каждый потребитель помнит, какое из двух смотреть)
  const off = useMemo(
    () => new Set((targets.data ?? []).map((t) => `${t.mp_code}|${t.region}`)),
    [targets.data],
  );
  const isOff = useCallback(
    (mp: string, region = ""): boolean => off.has(`${mp}|${region}`),
    [off],
  );
  const targetMut = useMutation({
    mutationFn: api.setTargetEnabled,
    onSuccess: (list) => {
      qc.setQueryData(KEYS.targets, list);
      // только отчёты спроса: пул готово/доупак запреты не читает, и его перезапрос был бы
      // лишним полным проходом по 1С. Движок — только если он включён.
      for (const k of [KEYS.wb, KEYS.oz, KEYS.ym])
        qc.invalidateQueries({ queryKey: k });
      if (engineOn) qc.invalidateQueries({ queryKey: KEYS.alloc });
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось сохранить настройку")),
  });

  // ── выборочное распределение: выделенные товары забирают дефицитный пул первыми ──
  const [sel, setSel] = usePersistedState<Set<string>>(
    SEL_STORAGE,
    new Set(),
    (raw) => new Set(JSON.parse(raw) as string[]),
    (v) => JSON.stringify([...v]),
  );
  const [onlySelected, setOnlySelected] = useState(false);
  // ПРИМЕНЁННЫЙ выбор — отдельно от текущего: он входит в ключ запроса движка, а тот считается
  // ~19с. Автоприменение означало бы полный пересчёт на каждый клик по чекбоксу.
  const [priorityNoms, setPriorityNoms] = useState<string[]>([]);

  // ── движок распределения (маржа-оптимум): ленивый — гоняет свои отчёты, включаем по стратегии ──
  const [selCh, setSelCh] = useState<Set<string>>(() => new Set());
  const [selReg, setSelReg] = useState<Set<string>>(() => new Set());
  //: Юрлицо отгрузки. Поставку делают сначала по одному юрлицу, потом по второму — а «Зафиксировать
  //: показанное» берёт РОВНО отфильтрованное, поэтому фильтр здесь и есть способ это сделать.
  const [selLegal, setSelLegal] = useState<Set<string>>(() => new Set());
  const [engine, setEngine] = useState<EngineMode>("off");
  //: «Распределить по остатку ГП» — главный режим, пока флаконная модель выключена. Кнопкой, а не
  //: автоматом: переход от «сколько надо в идеале» к «сколько реально уедет» человек должен
  //: сделать осознанно, иначе числа, на которые смотрят каждый день, поменяются молча.
  const [shelf, setShelf] = useState(false);
  const [floorDays, setFloorDays] = useState(prefs.floor_days);
  const [packPerDay, setPackPerDay] = useState(prefs.packaging_per_day);

  // Сохраняем ПРИМЕНЁННЫЕ значения (дебаунс отсечек уже отработал) и только когда они реально
  // разошлись с сохранёнными — иначе каждый рендер писал бы в БД одно и то же. Пока запрос в
  // полёте, не шлём второй: он всё равно перезапишет тем же, а после ответа эффект сверится заново.
  const prefsMutRef = useRef(prefsMut);
  prefsMutRef.current = prefsMut;
  useEffect(() => {
    const mut = prefsMutRef.current;
    if (mut.isPending) return;
    const next = {
      target: params.target,
      catalog: params.catalog,
      overstock: params.overstock,
      floor_days: floorDays,
      packaging_per_day: packPerDay,
    };
    const same =
      prefs.target === next.target &&
      prefs.catalog === next.catalog &&
      prefs.overstock === next.overstock &&
      prefs.floor_days === next.floor_days &&
      prefs.packaging_per_day === next.packaging_per_day;
    if (!same) mut.mutate(next);
    // зависимости — ЗНАЧЕНИЯ: объект `useMutation` пересоздаётся на каждом рендере, и с ним в
    // списке эффект прогонялся на каждый символ и на каждый кадр скролла
  }, [params, floorDays, packPerDay, prefs]);
  // флаг — часть условия, а не только обёртка JSX: иначе движок «выключен» лишь потому, что
  // единственный `setEngine` спрятан внутри скрытого блока
  const engineOn = (SUPPLY_UI.engine && engine !== "off") || shelf;
  const alloc = useQuery({
    queryKey: [
      ...KEYS.alloc,
      params,
      engine,
      floorDays,
      packPerDay,
      priorityNoms,
      shelf,
      [...selCh].sort(),
      [...selReg].sort(),
    ],
    queryFn: () =>
      api.getAllocation({
        ...params,
        // ⚠️ В режиме полки защита равна ГТЗ (решение владельца 30.10.2026): защитный проход идёт
        // по убыванию скорости продаж и добивает КАЖДЫЙ регион до ГТЗ, поэтому он и есть весь
        // план — ходовой регион забирает остаток первым, тихий получает, что останется. Прежде
        // защита стояла на двух неделях, и остаток после них раздавался по марже: при пуле меньше
        // суммарной потребности это давало «половина регионов с нулём» без объяснения.
        strategy: shelf ? "protect_then_margin" : (engine as api.AllocStrategy),
        floor_days: shelf ? params.target : floorDays,
        packaging_per_day: packPerDay,
        priority_nom: priorityNoms.length ? priorityNoms : undefined,
        ready_only: shelf || undefined,
        // ⚠️ Делим остаток между ВЫБРАННЫМ: менеджер ведёт свой канал и свои регионы, и раздача по
        // невыбранным отняла бы у него полку в пользу того, чего он сейчас не планирует. Пул при
        // этом полный. Обратная сторона: два менеджера по разным каналам разберут один остаток.
        channel: selCh.size ? [...selCh] : undefined,
        region: selReg.size ? [...selReg] : undefined,
      }),
    enabled: engineOn,
    staleTime: 5 * 60_000,
  });
  const engineReady = engineOn && alloc.data != null; // движок отработал (данные пришли)
  // Движок и таблица берут спрос WB разными запросами: источник переключили между ними — и
  // «Решения» с «Допоставить» посчитаны по разному спросу. Это видно (красным), а устаревший из
  // двух отчётов перезапрашиваем ОДИН раз на расхождение: если бэк упорно отдаёт разные источники,
  // предупреждение остаётся, а запросы по кругу не крутятся.
  const allocWbMismatch =
    engineOn &&
    alloc.data != null &&
    wbData != null &&
    alloc.data.wb_source !== wbData.wb_source;
  const mismatchSig = allocWbMismatch
    ? `${alloc.data?.wb_source}|${wbData?.wb_source}`
    : "";
  const mismatchHandled = useRef("");
  useEffect(() => {
    if (!mismatchSig || mismatchHandled.current === mismatchSig) return;
    if (wb.isFetching || alloc.isFetching) return;
    mismatchHandled.current = mismatchSig;
    qc.invalidateQueries({
      queryKey: alloc.dataUpdatedAt < wb.dataUpdatedAt ? KEYS.alloc : KEYS.wb,
    });
  }, [
    mismatchSig,
    wb.isFetching,
    alloc.isFetching,
    wb.dataUpdatedAt,
    alloc.dataUpdatedAt,
    qc,
  ]);
  // рекомендация по региону: ключ → строка движка (allocated + trace + reason)
  const allocMap = useMemo(() => {
    const m = new Map<string, api.AllocationLine>();
    for (const ln of alloc.data?.lines ?? [])
      m.set(decKey(ln.nom_key, ln.channel as Channel, ln.region), ln);
    return m;
  }, [alloc.data]);
  // все кандидаты движка по товару (для «конкуренции за пул» в «почему?»)
  const allocByNom = useMemo(() => {
    const m = new Map<string, api.AllocationLine[]>();
    for (const ln of alloc.data?.lines ?? []) {
      const arr = m.get(ln.nom_key);
      if (arr) arr.push(ln);
      else m.set(ln.nom_key, [ln]);
    }
    return m;
  }, [alloc.data]);
  const recOf = useCallback(
    (
      nom: string,
      ch: Channel,
      region: string,
    ): api.AllocationLine | undefined =>
      engineOn ? allocMap.get(decKey(nom, ch, region)) : undefined,
    [engineOn, allocMap],
  );
  // покрытие потребности рекомендацией (Σ дали / Σ нужно по всем кандидатам движка)
  const coveragePct = useMemo(() => {
    let need = 0,
      got = 0;
    for (const ln of alloc.data?.lines ?? []) {
      need += ln.need;
      got += ln.allocated;
    }
    return need > 0 ? Math.round((100 * got) / need) : 0;
  }, [alloc.data]);

  const anyFetching = wb.isFetching || oz.isFetching || ym.isFetching;
  const capFetching = cap.isFetching;
  const hasCap = Boolean(cap.data);
  // «доупак» посчитан только при наличии снапшота остатков 1С; иначе показываем «—», не 0
  const assembleComputed = Boolean(cap.data?.assemble_computed);
  // «—» означает РАЗНОЕ: снимка нет (не посчитали) либо товар вне флаконной модели (неприменимо).
  // Ноль показываем только когда доупак реально посчитан и равен нулю.
  const doupak = (v: number): string => (assembleComputed ? int0(v) : "—");

  // сохранённые решения: ключ → qty
  const saved = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dec.data ?? [])
      m.set(decKey(d.nom_key, d.channel as Channel, d.region), d.qty);
    return m;
  }, [dec.data]);
  // локальные правки (перебивают сохранённое до сейва)
  const [edited, setEdited] = useState<Map<string, number>>(() => new Map());
  const dropEdited = useCallback((key: string, onlyIfEquals?: number): void => {
    setEdited((m) => {
      // снимаем правку, только если её с тех пор не переписали: иначе значение, набранное во
      // время сохранения, потерялось бы молча (saveLeaf уже не увидел бы его в `edited`)
      if (
        !m.has(key) ||
        (onlyIfEquals !== undefined && m.get(key) !== onlyIfEquals)
      )
        return m;
      const next = new Map(m);
      next.delete(key);
      return next;
    });
  }, []);

  // точечный сброс и полная очистка — РАЗНЫЕ ручки: у массовой намеренно нет параметров, чтобы
  // потерянный на клиенте фильтр не превращал сброс одной строки в очистку всей таблицы
  const dropDecisionMut = useMutation({
    mutationFn: (k: { nom_key: string; channel: string; region: string }) =>
      api.deleteDecision(k),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.dec }),
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось сбросить решение")),
  });
  const clearMut = useMutation({
    mutationFn: () => api.clearDecisions(),
    onSuccess: ({ deleted }) => {
      qc.invalidateQueries({ queryKey: KEYS.dec });
      toast.success(
        deleted
          ? `Удалено сохранённых решений: ${deleted}`
          : "Сохранённых решений не было",
      );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось очистить")),
  });

  const saveMut = useMutation({
    mutationFn: (v: {
      nom_key: string;
      channel: string;
      region: string;
      qty: number;
    }) => api.putDecision(v),
    onSuccess: (row, v) => {
      // сохранённое значение кладём в кэш сразу (ручка возвращает строку) — иначе между снятием
      // правки и приходом рефетча ячейка мигала бы значением движка
      qc.setQueryData<api.SupplyDecision[]>(KEYS.dec, (prev) => {
        const rest = (prev ?? []).filter(
          (d) =>
            !(
              d.nom_key === row.nom_key &&
              d.channel === row.channel &&
              d.region === row.region
            ),
        );
        return [...rest, row];
      });
      dropEdited(decKey(v.nom_key, v.channel as Channel, v.region), v.qty);
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось сохранить решение")),
  });

  // снимок остатков 1С (нужен для «Доупак»). Кнопка здесь + автозапрос, если снимка нет.
  const snapMut = useMutation({
    mutationFn: api.refreshSnapshot,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["supply"] }); // весь снимок-зависимый кэш (доупак/движок)
      toast.success("Снимок остатков 1С обновлён");
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось снять остатки 1С")),
  });
  // автозапрос ОДИН раз: если снимок загрузился и его нет — снимаем сами (доупак иначе пустой)
  const autoSnap = useRef(false);
  useEffect(() => {
    if (!SUPPLY_UI.assembly) return; // доупаковка скрыта — снимок 1С не нужен
    if (
      !cap.isFetching &&
      cap.data &&
      !cap.data.assemble_computed &&
      !autoSnap.current &&
      !snapMut.isPending
    ) {
      autoSnap.current = true;
      snapMut.mutate();
    }
  }, [cap.isFetching, cap.data, snapMut]);

  // эффективное «Решение» листа: правка → сохранённое → рекомендация движка → «Допоставить»
  // ЕДИНСТВЕННАЯ цепочка решения: выключено → сохранено → рекомендация движка → потребность.
  // `decisionBase` — «зафиксированная» картина (без несохранённых правок): по ней сортируем и
  // фильтруем, иначе строки прыгали бы под курсором при вводе.
  const decisionBase = useCallback(
    (nom: string, ch: Channel, l: Leaf): number => {
      if (l.disabled) return 0; // выключенная цель не даёт решения НИКОМУ (иначе строка спорит с итогами)
      const k = decKey(nom, ch, l.region);
      const sv = saved.get(k);
      if (sv !== undefined) return sv;
      const rec = engineReady ? allocMap.get(k) : undefined;
      if (rec) return rec.allocated;
      // движок отработал, но строки нет → товар исключён (на вывод / нет 1С-моста) → 0, не полный need
      return engineReady ? 0 : Math.round(l.need);
    },
    [saved, engineReady, allocMap],
  );
  // то же плюс локальная правка — для показа в ячейке и для фиксации
  const decisionOf = useCallback(
    (nom: string, ch: Channel, l: Leaf): number =>
      l.disabled
        ? 0
        : (edited.get(decKey(nom, ch, l.region)) ?? decisionBase(nom, ch, l)),
    [edited, decisionBase],
  );

  // источник эффективного «Решения» листа (для подсветки: ручное/движок/потребность).
  // "stale" = сохранённое РАСХОДИТСЯ с текущей рекомендацией движка → значение устарело.
  const decisionSourceOf = useCallback(
    (nom: string, ch: Channel, l: Leaf): DecisionSource => {
      // выключенная цель всегда 0 (см. decisionOf) — источник «сохранено вручную: 100» врал бы
      // о числе, которого больше нет в силе. Сбросить осиротевшее решение всё равно можно:
      // крестик в ячейке показывается по наличию сохранённого, а не по источнику.
      if (l.disabled) return "zero";
      const k = decKey(nom, ch, l.region);
      if (edited.get(k) !== undefined) return "edited";
      const s = saved.get(k);
      if (s !== undefined) {
        const rec = engineReady ? allocMap.get(k) : undefined;
        if (rec !== undefined)
          return rec.allocated !== s ? "saved_stale" : "saved";
        // ⚠️ Движка нет — сверяем с ПОТРЕБНОСТЬЮ. Решение живёт дольше поставки: увезли ровно то,
        // что решили, товар попал в «в пути», потребность стала нулём — а сохранённое число
        // осталось и просится увезти столько же второй раз (боевой случай 15.09.2026: TV-617 в
        // Ростов — «в пути» 210, «Решение» 210, «Допоставить» 0).
        return s > Math.round(l.need) ? "saved_stale" : "saved";
      }
      const rec = engineReady ? allocMap.get(k) : undefined;
      if (rec) return "engine";
      if (engineReady) return "zero";
      return "need";
    },
    [edited, saved, engineReady, allocMap],
  );

  // Куда МОЖНО везти: полный список целей канала из отчёта (маппинг регионов / кластеров /
  // складов) плюс те, что реально встретились в данных, минус выключенные. Список не зависит от
  // товара: по нему ниже достраиваются НУЛЕВЫЕ строки — товар, которого в регионе ещё не было,
  // иначе некуда завезти вообще, а именно это и нужно для новинок и для расширения географии.
  const liveTargets = useMemo(() => {
    const reps: Record<Channel, api.WbAnalyticsReport | undefined> = {
      wb: wbData,
      ozon: oz.data,
      ym: ym.data,
    };
    const out = {} as Record<Channel, string[]>;
    for (const c of CHANNELS) {
      const rep = reps[c];
      if (!rep || isOff(c)) {
        out[c] = []; // канал выключен целиком — не достраиваем ему ничего
        continue;
      }
      const all = new Set([
        ...(rep.all_regions ?? []),
        ...rep.rows.map((r) => r.region),
      ]);
      // «Не известен» — не адрес, а мусорная корзина для несопоставленных складов/округов. Строку
      // туда достраивать нельзя: менеджер проставил бы «Решение», и поставка уехала бы в никуда.
      all.delete(UNKNOWN_REGION);
      out[c] = [...all]
        .filter((r) => !isOff(c, r))
        .sort((a, b) => a.localeCompare(b, "ru"));
    }
    return out;
  }, [wbData, oz.data, ym.data, isOff]);

  // ── слияние WB + Ozon + Яндекс в товары по nom_key ──
  // Возвращает не только товары: строки, которые в них НЕ попали, и строки, которые пришлось
  // сложить, — не молчаливый факт, а предупреждение на странице (см. `dataWarnings`).
  const { products, dropped, merged, seededNoSku, skuPairs } = useMemo(() => {
    const byNom = new Map<string, Product>();
    // Мост «канал + номенклатура → sku» — для карты юрлиц. Он приходит в тех же отчётах спроса,
    // и просить его у 1С отдельной ручкой стоило 31 секунду из 59 на каждом открытии экрана.
    const skuByKey = new Map<string, api.LegalPair>();
    // канал помним рядом: предупреждение про выключенный МП — шум, а не сигнал
    const dropped: { channel: Channel; label: string }[] = [];
    const merged: { channel: Channel; text: string }[] = [];
    const addRow = (r: api.WbAnalyticsRow, channel: Channel): void => {
      if (!r.nom_key) {
        // без номенклатуры 1С позицию не к чему привязать: ни пула, ни спецификации, ни решения
        dropped.push({
          channel,
          label: `${CH_LABEL[channel]}: ${r.mp_sku || r.article || "?"}`,
        });
        return;
      }
      let p = byNom.get(r.nom_key);
      if (!p) {
        p = {
          nom_key: r.nom_key,
          article: r.article,
          name: r.nom_name || r.mp_sku,
          channels: [],
          ready: 0,
          assemble: 0,
          orders: 0,
          stock: 0,
          supply: 0,
          need: 0,
          warnings: [],
          block: "",
          delisting: false,
          noSku: [],
          noMovement: false,
        };
        byNom.set(r.nom_key, p);
      }
      if (r.mp_sku)
        skuByKey.set(`${channel}\u0000${r.nom_key}`, {
          channel,
          nom_key: r.nom_key,
          mp_sku: String(r.mp_sku),
        });
      if (!p.name && (r.nom_name || r.mp_sku)) p.name = r.nom_name || r.mp_sku;
      if (!p.article && r.article) p.article = r.article;
      let ch = p.channels.find((c) => c.channel === channel);
      if (!ch) {
        ch = { channel, regions: [], orders: 0, stock: 0, supply: 0, need: 0 };
        p.channels.push(ch);
      }
      // Отчёт считает по (регион × mp_sku), а решение и поставка живут на (номенклатура × канал ×
      // регион) — значит лист тут обязан быть ОДИН. Если у номенклатуры две позиции МП в одном
      // регионе, складываем факты (штуки в день аддитивны, глубина выводится заново) и говорим
      // вслух: иначе вторая позиция либо потерялась бы в дереве, либо ушла бы в фиксацию ВТОРОЙ
      // строкой на тот же адрес.
      const prev = ch.regions.find((x) => x.region === r.region);
      if (prev) {
        prev.orders += r.orders_qty;
        prev.stock += r.stock_qty;
        prev.supply += r.supply_qty;
        prev.need += r.supply_result;
        prev.speed += r.selling_speed;
        prev.speed_7d += r.speed_7d;
        prev.speed_30d += r.speed_30d;
        prev.days_avail = Math.max(prev.days_avail, r.days_avail);
        prev.depth = prev.speed > 0 ? prev.stock / prev.speed : DEPTH_INF;
        prev.paid = addPaid(prev.paid, r.paid_storage);
        merged.push({
          channel,
          text:
            `${CH_LABEL[channel]}/${r.region}, ${r.article || r.nom_key}: две позиции МП на одну` +
            ` номенклатуру — числа сложены${prev.in_box !== r.in_box ? `, кванты разные (${prev.in_box}/${r.in_box})` : ""}`,
        });
      } else {
        ch.regions.push({
          channel,
          region: r.region,
          orders: r.orders_qty,
          stock: r.stock_qty,
          supply: r.supply_qty,
          days_avail: r.days_avail,
          speed: r.selling_speed,
          speed_7d: r.speed_7d,
          speed_30d: r.speed_30d,
          depth: r.stock_depth,
          need: r.supply_result,
          in_box: r.in_box,
          disabled: Boolean(r.disabled),
          paid: addPaid(undefined, r.paid_storage),
        });
      }
      ch.orders += r.orders_qty;
      ch.stock += r.stock_qty;
      ch.supply += r.supply_qty;
      ch.need += r.supply_result;
      p.orders += r.orders_qty;
      p.stock += r.stock_qty;
      p.supply += r.supply_qty;
      p.need += r.supply_result;
    };
    for (const r of wbData?.rows ?? []) addRow(r, "wb");
    for (const r of oz.data?.rows ?? []) addRow(r, "ozon");
    for (const r of ym.data?.rows ?? []) addRow(r, "ym");

    // Товары БЕЗ движения ни на одной площадке (новинка в производстве, NGG-142): в отчётах их нет
    // вовсе, сеем из карточек 1С ПУСТЫМИ — каналы им заведёт достройка нулевых строк ниже (по мосту
    // sku, с бейджем «нет артикула»), а пул подцепится в цикле пула (он по позициям 1С, не по
    // отчётам). Сеем ДО пула, иначе у новинки было бы «готово 0» при полном складе.
    const seeded = new Set<string>();
    // ⚠️ Хоть один отчёт спроса не загрузился → не сеем вовсе: товар, движущийся только на
    // упавшем канале, выглядел бы «без движения» — молчаливая маскировка ошибки (см. dataWarnings).
    const seedCards = failedDemand.length ? [] : (cards.data ?? []);
    for (const s of newProductSeeds(seedCards, (n) => byNom.has(n))) {
      byNom.set(s.nom_key, {
        nom_key: s.nom_key,
        article: s.article,
        name: s.name,
        channels: [],
        ready: 0,
        assemble: 0,
        orders: 0,
        stock: 0,
        supply: 0,
        need: 0,
        warnings: [],
        block: "",
        delisting: false,
        noSku: [],
        noMovement: true,
      });
      seeded.add(s.nom_key);
    }

    // единый пул готово/доупак на товар (по nom_key; один запрос, общий между каналами)
    for (const it of Object.values(cap.data?.items ?? {})) {
      const p = byNom.get(it.nom_key);
      if (p) {
        p.ready = it.ready_now;
        p.assemble = it.can_assemble;
        // у товара может быть несколько позиций (mp_sku): берём ХУДШЕЕ состояние, а не последнее
        // по порядку — дефект данных не должен теряться из-за соседней здоровой позиции
        p.block = worseBlock(p.block, it.assembly_block || "");
        if (it.delisting) p.delisting = true;
        for (const w of it.warnings)
          if (!p.warnings.includes(w)) p.warnings.push(w);
      }
    }
    // Достраиваем НУЛЕВЫЕ строки на все включённые цели: строка «нет данных» и строка «сюда не
    // возим» — разные вещи, а раньше выглядели одинаково (обеих просто не было). Квант берём у
    // соседней строки того же товара: он свойство упаковки, а не региона, — иначе «Коробов» на
    // новой цели остался бы прочерком.
    for (const p of byNom.values()) {
      for (const channel of CHANNELS) {
        const want = liveTargets[channel];
        if (want.length === 0) continue;
        // Канал берём, если у товара есть КАРТОЧКА на нём: либо он уже торгуется (строки из
        // отчёта), либо карточка заведена, но движения ещё не было — тогда канала в отчёте нет
        // вовсе, и раньше завезти туда было нечем (NMM-311: «Готов к продаже» на Ozon, остаток 0,
        // продаж нет — на экране один WB). Без моста sku канал НЕ заводим: поставка ушла бы в файл
        // маркетплейса без артикула и без юрлица.
        let ch = p.channels.find((c) => c.channel === channel);
        if (!ch) {
          const sku = cardSku.get(`${channel}\u0000${p.nom_key}`);
          if (!sku) {
            // спека говорит «возим», а моста нет — называем это вслух на строке товара
            if (
              cardNoSku.has(`${channel}\u0000${p.nom_key}`) &&
              !p.noSku.includes(channel)
            )
              p.noSku.push(channel);
            continue;
          }
          ch = {
            channel,
            regions: [],
            orders: 0,
            stock: 0,
            supply: 0,
            need: 0,
          };
          p.channels.push(ch);
          // мост для карты юрлиц: у достроенного канала строк отчёта нет, и без этой записи
          // фильтр показал бы «юрлицо не определилось» на позиции, у которой оно определимо
          skuByKey.set(`${channel}\u0000${p.nom_key}`, {
            channel,
            nom_key: p.nom_key,
            mp_sku: sku,
          });
        }
        const have = new Set(ch.regions.map((l) => l.region));
        // квант берём у строки ЭТОГО канала: у соседнего МП упаковка своя, и подставлять её было
        // бы выдумкой. Соседей нет (новинка, канал без движения) — со спеки канала в 1С; нет и
        // там — 0, ячейка честно покажет прочерк
        const inBox = fillInBox(
          ch.regions,
          cardInBox.get(cardKey(channel, p.nom_key)),
        );
        for (const region of want) {
          if (have.has(region)) continue;
          ch.regions.push({
            channel,
            region,
            orders: 0,
            stock: 0,
            supply: 0,
            days_avail: 0,
            speed: 0,
            speed_7d: 0,
            speed_30d: 0,
            depth: DEPTH_INF,
            need: 0,
            in_box: inBox,
            disabled: false,
          });
        }
      }
      p.channels.sort((a, b) => a.channel.localeCompare(b.channel));
      // нулевые уезжают вниз сами: сортировка по «Допоставить», у них он 0
      for (const ch of p.channels) ch.regions.sort((a, b) => b.need - a.need);
    }
    // Засеянный товар без единого канала в таблицу не попадёт: она строится из строк. Если причина —
    // «нет артикула», говорим вслух в предупреждениях данных; если у его каналов просто нет
    // включённых целей — планировать нечего, убираем (`seededVerdict`).
    const seededNoSku: { label: string; noSku: Channel[] }[] = [];
    for (const nom of seeded) {
      const p = byNom.get(nom)!;
      const verdict = seededVerdict(p);
      if (verdict === "show") continue;
      if (verdict === "warn")
        seededNoSku.push({ label: p.article || p.name || nom, noSku: p.noSku });
      byNom.delete(nom);
    }
    return {
      products: Array.from(byNom.values()),
      dropped,
      merged,
      seededNoSku,
      skuPairs: [...skuByKey.values()],
    };
  }, [
    wbData,
    oz.data,
    ym.data,
    cap.data,
    cards.data,
    failedDemand.length,
    liveTargets,
    cardSku,
    cardNoSku,
    cardInBox,
  ]);

  // Каким юрлицом поедет позиция. Тем же каскадом, что и фиксация, — иначе фильтр обещал бы одно,
  // а поставка уехала бы на другое юрлицо. Меняется редко (остатки/заказы за сутки), держим долго.
  const legals = useQuery({
    // ключ — число пар, а не сами пары: список из шестисот объектов в ключе react-query хэширует
    // на каждом рендере, а меняется он ровно вместе с отчётами
    queryKey: [...KEYS.legals, skuPairs.length],
    queryFn: () => api.getLegalsMap(skuPairs),
    enabled: skuPairs.length > 0,
    staleTime: 30 * 60_000,
  });
  const legalHint = legals.isError
    ? `Карта юрлиц не загрузилась: ${extractErrorMessage(legals.error, "ошибка")}. Фильтровать не по чему — нажмите «Обновить»`
    : legals.isFetching
      ? "Спрашиваем DWH, каким юрлицом поедет каждая позиция"
      : "У показанных позиций одно юрлицо — фильтровать не по чему";
  const legalOptions = useMemo(() => {
    const rows = legals.data ?? [];
    const named = [
      ...new Set(rows.map((r) => r.legal_name).filter(Boolean)),
    ].sort();
    // «не определилось» — такой же выбор, а не мусор: по этим позициям заказ 1С заблокируется,
    // и найти их надо уметь ДО фиксации, а не по факту
    return rows.some((r) => !r.legal_name) ? [...named, ""] : named;
  }, [legals.data]);
  const legalOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of legals.data ?? [])
      m.set(`${r.channel}\u0000${r.nom_key}`, r.legal_name);
    return m;
  }, [legals.data]);

  /** Выделение переживает перезагрузку, поэтому в нём копятся ключи товаров, которых в текущих
   *  данных уже нет (сняли с продажи, выпал из отчёта). Считаем только ЖИВЫЕ: иначе счётчик
   *  выделения тихо завышался бы навсегда. Пока данные грузятся, сверять не с чем — не подрезаем.
   *  ЖИВОЕ ≠ ВИДИМОЕ: действия берут `selShown` (живое ∩ прошедшее фильтры), см. ниже. */
  const selLive = useMemo(
    () =>
      products.length === 0
        ? sel
        : visibleSelection(sel, products, (p) => p.nom_key),
    [sel, products],
  );

  // «вне модели» (норма) и «спека сломана» (дефект данных) — РАЗНЫЕ состояния, показываем раздельно.
  // Списки и счётчики берём из ОДНОГО источника (`products`): считать по `cap.data.items`, а
  // показывать из `products` — значит позволить заголовку «3 товара» стоять над двумя чипами.
  const blocked = useMemo(
    () => ({
      no_base: products.filter((p) => p.block === "no_base"),
      spec_problem: products.filter((p) => p.block === "spec_problem"),
    }),
    [products],
  );

  // ── фильтры ──
  const [showHelp, setShowHelp] = useState(false);
  const [search, setSearch] = useState("");
  // регионы по КАНАЛУ (код, а не подпись) — примитив, из которого строятся и фильтр, и настройки.
  // В настройки нужен полный список: `all_regions` из отчёта (весь маппинг) ∪ живые регионы строк
  // ∪ уже выключенные. Без последнего слагаемого регион, выключенный и потом выпавший из отчётов,
  // нельзя было бы включить обратно ничем, кроме SQL.
  // Регионы канала = включённые цели (`liveTargets`): строки товара теперь достраиваются ровно по
  // ним, поэтому обходить для этого 11 тыс. листьев — та же работа второй раз. Выключенные
  // добавляет `settingsRegions` ниже, из списка запретов.
  const regionsByChannel = regionsFromTargets(liveTargets);
  // То же плюс ВЫКЛЮЧЕННЫЕ: их в `liveTargets` нет по определению, а в настройках они обязаны
  // быть — иначе выключенный регион нечем включить обратно.
  const settingsRegions = useMemo<Record<Channel, string[]>>(() => {
    const out = {} as Record<Channel, string[]>;
    for (const c of CHANNELS) {
      const all = new Set(liveTargets[c]);
      for (const t of targets.data ?? [])
        if (t.mp_code === c && t.region) all.add(t.region);
      out[c] = [...all].sort((a, b) => a.localeCompare(b, "ru"));
    }
    return out;
  }, [liveTargets, targets.data]);
  const regionGroups = useMemo(() => {
    return [
      { label: "Wildberries — регионы", items: regionsByChannel.wb },
      { label: "Ozon — кластеры", items: regionsByChannel.ozon },
      { label: "Яндекс.Маркет — склады", items: regionsByChannel.ym },
    ].filter((g) => g.items.length > 0);
  }, [regionsByChannel]);

  // Агрегатные скорость/глубина на товаре/канале/ИТОГО — по СУММЕ скоростей регионов.
  // Лист несёт backend-овскую `max(заказы_7/7, заказы_30/30)` (с 15.10.2026; до того —
  // `orders/дней_в_наличии`), и max берётся ПО РЕГИОНУ: агрегат, пересчитанный как заказы/окно,
  // получался бы МЕНЬШЕ суммы своих листьев, и глубина строки товара завышалась бы относительно её
  // же регионов — вплоть до «перетарки» на товаре, когда ни один регион не за потолком.
  // Штуки в день аддитивны по регионам, поэтому суммируем их (`aggValues`), а не выводим заново.

  // ── состояние таблицы (сортировка / фильтры / видимость колонок) ──────────
  const [sorting, setSorting] = useState<SortingState>([
    { id: "decision", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =
    usePersistedState<VisibilityState>(
      VIS_STORAGE,
      DEFAULT_VISIBILITY,
      (raw) => JSON.parse(raw) as VisibilityState,
      JSON.stringify,
    );
  const [tableExpanded, setTableExpanded] = useState<ExpandedState>({});
  const [groupMode, setGroupMode] = usePersistedState<GroupMode>(
    GROUP_STORAGE,
    "prod-ch-reg",
    (raw) =>
      GROUP_MODES.some(([m]) => m === raw) ? (raw as GroupMode) : undefined,
    (m) => m,
  );
  // Ключи строк у режимов разные — старое раскрытие к ним неприменимо. Сбрасывает его КНОПКА, а не
  // эффект по `groupMode`: эффект отрабатывал и на монтировании, и первый же рендер страницы зря
  // пересобирал дерево вместе со всеми моделями строк react-table.
  const switchGroup = (m: GroupMode): void => {
    setGroupMode(m);
    setTableExpanded({});
  };

  const isExpanded = useCallback(
    (key: string): boolean =>
      tableExpanded === true ||
      Boolean((tableExpanded as Record<string, boolean>)[key]),
    [tableExpanded],
  );
  // `sel` влияет на состав только при включённом «показывать только выбранные»: иначе галка
  // меняла бы идентичность зависимости и гнала полную пересборку дерева впустую
  const selFilter = onlySelected && sel.size > 0 ? sel : null;
  // Выключенные регионы по умолчанию НЕ показываем: у канала их бывают десятки, и строки с
  // нулями забивают экран. Но и не прячем молча — на строке канала стоит счётчик «N выкл.»,
  // а переключатель живёт в том же попапе, где их выключают.
  const [showOff, setShowOff] = useState(false);
  /** «Скрыть у себя» — фильтр ОБЗОРА (этот человек, этот браузер). Обычный фильтр: строки
   *  исчезают из таблицы, из итогов и из «выделенное ∩ видимое», но потребность, «Решения» и
   *  движок не трогает — скрытое они видят. ⚠️ Фиксация же увозит РОВНО показанное (кнопка «что
   *  видно, то и уедет»), и про скрытое она говорит вслух — `fixationScope`. Личное и «Куда
   *  планируем» (тоже личное, с 17.09.2026), но там выключенное меняет РАСЧЁТ: пропадает из
   *  потребности и фиксации. */
  const [hidden, setHidden] = usePersistedState<HiddenByMe>(
    HIDE_STORAGE,
    NOTHING_HIDDEN,
    parseHidden,
    stringifyHidden,
  );
  const hideCounts = hiddenCounts(hidden);
  const hideChannels = useMemo<HideChannel[]>(
    () =>
      CHANNELS.map((c) => ({
        code: c,
        label: CH_LABEL[c],
        badge: CH_BADGE[c],
        regions: settingsRegions[c],
      })),
    [settingsRegions],
  );

  // «Решение» для СОРТИРОВКИ и ФИЛЬТРОВ берём БЕЗ локальных правок: иначе каждое нажатие клавиши
  // пересобирало бы дерево и строки прыгали бы под курсором. Отображение и фиксация используют
  // `decisionOf` (с правками) — то есть таблица сортируется по зафиксированной картине.

  // значения листа/товара для фильтров по колонкам (один источник — не расходится с ячейками)
  // Один проход: собираем ВЫЖИВШИЕ листья (товар × канал × регион), затем группируем их по
  // выбранному режиму. Фильтры, числа и фиксация от режима не зависят — меняется только вложенность.
  const { tree, filtered, recs } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const prodF = columnFilters.filter((f) => f.id in PRODUCT_VALUE);
    const leafF = columnFilters.filter((f) => f.id in LEAF_VALUE);
    const recs: Rec[] = [];
    const prods: Product[] = [];

    // Личное скрытие применяем ПЕРВЫМ и одной функцией (`visibleProducts`): канал без видимых
    // регионов и товар без видимых каналов исчезают целиком, числа остаются на месте — агрегаты
    // ниже всё равно пересобираются по выжившим листьям.
    for (const { product: p, channels } of visibleProducts(
      hidden,
      products,
      (x) => x.channels,
    )) {
      if (selFilter && !selFilter.has(p.nom_key)) continue;
      if (
        q &&
        !p.name.toLowerCase().includes(q) &&
        !p.article.toLowerCase().includes(q)
      )
        continue;
      if (
        !prodF.every((f) =>
          passesFilter(
            PRODUCT_VALUE[f.id]!.kind,
            PRODUCT_VALUE[f.id]!.get(p),
            f.value,
          ),
        )
      )
        continue;
      const mine: typeof recs = [];
      const chGroups: ChGroup[] = [];
      for (const c of channels) {
        if (selCh.size > 0 && !selCh.has(c.channel)) continue;
        // Юрлицо — свойство пары (канал, товар): один и тот же товар на WB и Ozon может ехать с
        // разных организаций. Поэтому фильтр стоит ЗДЕСЬ, а не на товаре целиком.
        if (
          selLegal.size > 0 &&
          !selLegal.has(legalOf.get(`${c.channel}\u0000${p.nom_key}`) ?? "")
        )
          continue;
        const kept: Leaf[] = [];
        for (const l of c.regions) {
          if (selReg.size > 0 && !selReg.has(l.region)) continue;
          const v = leafValues(l, decisionBase(p.nom_key, c.channel, l));
          if (
            !leafF.every((f) => passesFilter("number", v[f.id] ?? 0, f.value))
          )
            continue;
          // выключенные оставляем в `mine`, чтобы их МОЖНО было показать переключателем и видеть,
          // чего стоит запрет. В суммы они не идут: это делают `sumLeaves` и `aggValues`
          mine.push({ p, ch: c.channel, l, v });
          kept.push(l);
        }
        if (kept.length)
          chGroups.push({
            channel: c.channel,
            regions: kept,
            ...sumLeaves(kept),
          });
      }
      if (mine.length === 0) continue;
      // `...p` сохраняет и `noSku`, и `delisting`: это свойства товара, а не выживших строк
      const prod: Product = {
        ...p,
        channels: chGroups,
        ...sumLeaves(chGroups.flatMap((c) => c.regions)),
      };
      prods.push(prod);
      for (const r of mine) r.p = prod; // ссылка на пересобранный товар (агрегаты по выжившим)
      recs.push(...mine);
    }

    const byKey = <T,>(xs: T[], k: (x: T) => string): Map<string, T[]> => {
      const m = new Map<string, T[]>();
      for (const x of xs) {
        const key = k(x);
        (m.get(key) ?? m.set(key, []).get(key)!).push(x);
      }
      return m;
    };

    // Один рекурсивный проход по уровням режима. `all` — ВСЕ дети (агрегат берёт из них только
    // включённые цели: выключенная не даёт ни фактов, ни плана), `vis` — что видно (скрытие
    // выключенных на числа не влияет), `off` — сколько скрыто.
    const levels = LEVELS[groupMode];
    const build = (
      rs: Rec[],
      depth: number,
      prefix: string,
      inherited: Partial<TRow>,
    ): { all: TRow[]; vis: TRow[]; off: number } => {
      const lv = levels[depth]!;
      const leafLevel = depth === levels.length - 1;
      const all: TRow[] = [];
      const vis: TRow[] = [];
      let off = 0;
      for (const [part, group] of byKey(rs, lv.keyOf)) {
        const key = joinKey(prefix, part);
        const head = group[0]!;
        const common = {
          ...inherited,
          ...fixedBy(lv.dim, head),
          dim: lv.dim,
          key,
        };
        if (leafLevel) {
          // В группе последнего уровня ровно одна запись: уровни покрывают всю тройку (товар ×
          // канал × регион), а лист по ней уникален по построению — две позиции МП на одну
          // номенклатуру в одном регионе складываются ещё при сборке товаров.
          const leaf: TRow = {
            ...common,
            v: head.v,
            subRows: [],
            l: head.l,
            paid: head.l.paid,
          };
          all.push(leaf);
          if (head.l.disabled) {
            off += 1;
            if (!showOff) continue; // строку прячем; в агрегат родителя она и так не идёт
          }
          vis.push(leaf);
          continue;
        }
        const kid = build(group, depth + 1, key, common);
        const node: TRow = {
          ...common,
          v: aggValues(kid.all),
          // справка, а не план: суммируем все входящие строки, включая выключенные цели —
          // платят за товар, который там лежит, независимо от «Куда планируем»
          paid:
            common.region !== undefined
              ? sumPaid(group.map((x) => x.l.paid))
              : undefined,
          off: kid.off,
          nKids: kid.vis.length,
          // subRows материализуем ТОЛЬКО у раскрытых: react-table строит Row и сортирует ВСЕ
          // уровни независимо от раскрытия — на 6.6к узлов это ~31 мс на нажатие против ~1 мс
          subRows: isExpanded(key) ? kid.vis : [],
        };
        all.push(node);
        off += kid.off;
        // Узел без единого видимого ребёнка не показываем. Это однозначно: строки есть на ВСЕ
        // включённые цели (пусть и нулевые), поэтому пусто здесь = «всё выключено», а выключенное
        // показывать не просили. Исключение — строка ТОВАРА (не «верхний уровень»: в режиме
        // «Канал: Регион → Товар» наверху регион, и по прежнему правилу выключенные регионы
        // оставались висеть пустыми строками). Товар оставляем: пропасть из таблицы он не должен,
        // даже если возить его сейчас некуда — иначе он молча исчезает из поля зрения.
        if (kid.vis.length > 0 || lv.dim === "product") vis.push(node);
      }
      return { all, vis, off };
    };
    return { tree: build(recs, 0, "", {}).vis, filtered: prods, recs };
  }, [
    products,
    search,
    selCh,
    selReg,
    selLegal,
    legalOf,
    selFilter,
    columnFilters,
    decisionBase,
    isExpanded,
    showOff,
    hidden,
    groupMode,
  ]);

  /** ВЫДЕЛЕННОЕ ∩ ВИДИМОЕ — то единственное, с чем работают массовые действия над выделением
   *  («Зафиксировать выбранное», «Распределить выбранное») и их счётчики. Менеджер выделял
   *  строки, потом ставил фильтры, видел 3 позиции — а кнопка обещала и фиксировала 14, включая
   *  скрытые. Галочки скрытых при этом целы: снимут фильтр — вернутся и в счёт, и в действие. */
  const selShown = useMemo(
    () => visibleSelection(sel, filtered, (p) => p.nom_key),
    [sel, filtered],
  );
  /** Сколько выделенного спрятано текущими фильтрами. Не «на всякий случай», а чтобы разница
   *  между галочками и объёмом действия была НАПИСАНА на экране, а не обнаружена по факту. */
  const selHidden = selLive.size - selShown.size;
  // сравниваем с ВИДИМЫМ выбором: в движок по кнопке уходит он же
  const selDirty = useMemo(() => {
    if (selShown.size !== priorityNoms.length) return true;
    return priorityNoms.some((n) => !selShown.has(n));
  }, [selShown, priorityNoms]);

  // Раскрыть всё до уровня N: раскрываем узлы всех уровней ВЫШЕ него (N=0 — свернуть всё).
  // Раскрывать 367 товаров кликами нереально, а разворот целиком нужен для поиска глазами.
  // Кнопки строятся из `LEVELS`, поэтому в режиме без отдельного канала кнопки «маркетплейса»
  // просто нет — раньше она была и молча делала ровно то же, что соседняя.
  const expandTo = useCallback(
    (upto: number): void => {
      const next: Record<string, boolean> = {};
      for (const r of recs)
        for (const k of pathKeys(groupMode, r).slice(0, upto)) next[k] = true;
      setTableExpanded(next);
    },
    [recs, groupMode],
  );

  // подсветка глубины: 🔴 риск пустоты (< порог защиты), 🟡 перетарка/заморозка (> потолок).
  const depthClass = (d: number): string =>
    d < floorDays
      ? "text-red-600 dark:text-red-400 font-medium"
      : d > params.overstock
        ? "text-amber-600 dark:text-amber-400"
        : "";

  const setLeafDecision = (
    nom: string,
    ch: Channel,
    region: string,
    qty: number,
  ): void => {
    setEdited((prev) => new Map(prev).set(decKey(nom, ch, region), qty));
  };
  const saveLeaf = (nom: string, ch: Channel, region: string): void => {
    const q = edited.get(decKey(nom, ch, region));
    if (q === undefined) return;
    saveMut.mutate({
      nom_key: nom,
      channel: ch,
      region,
      qty: Math.max(0, Math.round(q)),
    });
  };

  // Лист по ключу правки. Строится ОДИН раз на загрузку данных: без него дельта ниже обходила бы
  // все ~6 тыс. листьев на каждое нажатие клавиши, чтобы найти среди них 1–5 правок.
  const leafIndex = useMemo(() => {
    const m = new Map<string, RecKey>();
    for (const p of products)
      for (const c of p.channels)
        for (const l of c.regions)
          m.set(decKey(p.nom_key, c.channel, l.region), {
            p,
            ch: c.channel,
            l,
          });
    return m;
  }, [products]);

  // Насколько несохранённые правки сдвигают «Решение» КАЖДОГО узла дерева. Дерево при вводе НЕ
  // пересобирается (иначе строки прыгали бы под курсором), поэтому агрегаты берут своё число из
  // `r.v` плюс эту дельту — без неё родитель замирал, пока ИТОГО уже пересчитан.
  const deltaByKey = useMemo(() => {
    const m = new Map<string, number>();
    const bump = (k: string, d: number): Map<string, number> =>
      m.set(k, (m.get(k) ?? 0) + d);
    // идём ПО ПРАВКАМ (их единицы), а не по всем ~6 тыс. листьев: этот проход повторяется на
    // каждое нажатие клавиши в «Решении»
    for (const key of edited.keys()) {
      const hit = leafIndex.get(key);
      if (!hit) {
        // правка есть, а листа под неё нет — данные перезагрузились без этого товара/региона.
        // Говорим вслух: иначе введённое число просто исчезло бы из агрегатов без следа.
        console.warn(
          `[distribution] правка «${key}» не нашла своего листа — данные обновились?`,
        );
        continue;
      }
      const { ch, l } = hit;
      const nom = hit.p.nom_key;
      // разница ТЕХ ЖЕ двух величин, что стоят в ячейке и в `r.v`: живое решение минус базовое.
      // Пока дельта выводила цепочку решения заново, ей приходилось повторять и гард
      // «выключенная цель = 0» — теперь расхождение с ячейкой структурно невозможно.
      const d = decisionOf(nom, ch, l) - decisionBase(nom, ch, l);
      if (d === 0) continue;
      // те же ключи, что у узлов дерева; последний — сам лист, он берёт правку напрямую
      for (const k of pathKeys(groupMode, hit).slice(0, -1)) bump(k, d);
    }
    return m;
  }, [edited, leafIndex, decisionOf, decisionBase, groupMode]);

  /** «Живое» решение узла: у листа — с несохранённой правкой, у агрегата — посчитанная при сборке
   *  сумма плюс дельта правок. ОДНА формула на все производные колонки: пока копий было три, одна
   *  из них («остаток пула») считалась без правок и спорила с соседней ячейкой той же строки. */
  const liveDecision = (r: TRow): number =>
    r.l
      ? decisionOf(r.p!.nom_key, r.chCode!, r.l)
      : (r.v.decision ?? 0) + (deltaByKey.get(r.key) ?? 0);

  // ── колонки. Ячейки читают волатильные колбэки через ref: иначе `columns` пересобирался бы
  // на каждое нажатие клавиши в «Решении» (оно живёт в `edited`), а с ним и вся таблица.
  const ctx = {
    decisionOf,
    decisionSourceOf,
    recOf,
    allocByNom,
    liveDecision,
    depthClass,
    doupak,
    hasCap,
    engineOn,
    engineReady,
    shelf,
    sel,
    setSel,
    saved,
    dropEdited,
    dropDecisionMut,
    setLeafDecision,
    saveLeaf,
  };
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const columns = useMemo<ColumnDef<TRow>[]>(() => {
    const keep = keepFilter<TRow>();
    // ПРАВИЛО на все производные от «Решения» колонки: показываем ЖИВОЕ значение (с
    // несохранённой правкой), а сортируем и фильтруем по зафиксированному (`r.v`). Иначе либо
    // строки прыгают под курсором при вводе, либо ячейка замирает, пока ИТОГО уже пересчитался.
    const liveDecision = (r: TRow): number => ctxRef.current.liveDecision(r);
    const num = (
      id: string,
      title: string,
      cell?: ColumnDef<TRow>["cell"],
    ): ColumnDef<TRow> => ({
      id,
      accessorFn: (r) => r.v[id] ?? 0,
      filterFn: keep,
      meta: { title, filter: "number" } satisfies ColMeta,
      cell: cell ?? (({ getValue }) => int0(getValue<number>())),
    });
    const depth = (
      id: string,
      title: string,
      live?: boolean,
    ): ColumnDef<TRow> =>
      num(id, title, ({ row, getValue }) => {
        const r = row.original;
        const d = live
          ? futureDepth(
              r.v.stock ?? 0,
              r.v.supply ?? 0,
              liveDecision(r),
              r.v.speed ?? 0,
            )
          : getValue<number>();
        return (
          <span
            className={ctxRef.current.depthClass(d)}
            title={
              live
                ? "На сколько дней хватит ПОСЛЕ поставки: (остаток + в пути + решение) / скорость"
                : undefined
            }
          >
            {depthFmt(d)}
          </span>
        );
      });
    const cols: ColumnDef<TRow>[] = [
      {
        id: "article",
        accessorFn: (r) => r.p?.article ?? "",
        filterFn: keep,
        meta: {
          title: "Артикул",
          filter: "list",
          left: true,
        } satisfies ColMeta,
        cell: ({ row }) => {
          const r = row.original;
          // чекбокс выбора живёт на строке товара, в каком бы режиме она ни оказалась
          if (r.dim !== "product" || !r.p) return null;
          const c = ctxRef.current;
          return (
            <span className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={c.sel.has(r.p!.nom_key)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const next = new Set(c.sel);
                  if (e.target.checked) next.add(r.p!.nom_key);
                  else next.delete(r.p!.nom_key);
                  c.setSel(next);
                }}
                title="Выбрать товар для распределения в первую очередь"
              />
              <span className="truncate">{r.p!.article}</span>
            </span>
          );
        },
      },
      {
        id: "name",
        // сортировка составного уровня — по ключу «канал_регион», как он и показан
        accessorFn: (r) =>
          r.dim === "product"
            ? (r.p?.name ?? "")
            : r.dim === "channel"
              ? CH_LABEL[r.chCode!]
              : `${CH_LABEL[r.chCode!]} ${r.region ?? ""}`,
        filterFn: keep,
        meta: { title: "Товар", filter: "text", left: true } satisfies ColMeta,
        cell: ({ row }) => {
          const r = row.original;
          const open = row.getIsExpanded();
          const chevron = row.getCanExpand() ? (
            open ? (
              <ChevronDown className="size-3.5 shrink-0" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0" />
            )
          ) : null;
          const badge = r.chCode ? (
            <span
              className={cn(
                "shrink-0 rounded px-1.5 text-xs",
                CH_BADGE[r.chCode],
              )}
            >
              {CH_LABEL[r.chCode]}
            </span>
          ) : null;
          // ⚠️ «все выключены» — отдельная подпись, а не тот же счётчик. У такой строки нет
          // стрелки (раскрывать нечего) и нули во всех колонках, и без слова «все» это читается
          // как поломка: владелец спросил именно об этом.
          const allOff = (r.off ?? 0) > 0 && (r.nKids ?? 0) === 0;
          // ⚠️ Строка без детей ОБЯЗАНА объяснить себя. Причин три, и каждая — свой бейдж:
          // все цели выключены / в спеке канал есть, а моста sku нет / у канала не осталось
          // включённых целей вовсе. Раньше товар просто не раскрывался, и понять почему было
          // нечем (владелец: «чтоб не было такого, что позиция есть и просто не раскрывается»).
          const noSku = r.dim === "product" ? (r.p!.noSku ?? []) : [];
          const mute =
            (r.nKids ?? 0) === 0 &&
            (r.off ?? 0) === 0 &&
            noSku.length === 0 &&
            r.dim === "product";
          const offBadge = (r.off ?? 0) > 0 && (
            <span
              className={cn(
                "shrink-0 rounded px-1 text-[10px] font-medium",
                allOff
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                  : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
              )}
              title={
                allOff
                  ? `Вы выключили ВСЕ цели (${r.off}) — возить некуда, поэтому нули и строка не раскрывается. Включить или посмотреть их: «Куда планируем».`
                  : "Регионы выключены в вашем «Куда планируем» и скрыты. Показать их можно там же."
              }
            >
              {allOff ? `все ${r.off} выкл.` : `${r.off} выкл.`}
            </span>
          );
          if (r.dim === "product") {
            const loss =
              ctxRef.current.engineOn &&
              (ctxRef.current.allocByNom.get(r.p!.nom_key) ?? []).some(
                (l) => (traceNum(l.trace, "маржа_на_штуку") ?? 0) < 0,
              );
            return (
              <span
                className={cn(
                  "flex items-center gap-1 truncate",
                  r.l && "pl-6",
                )}
              >
                {chevron}
                <span className="truncate">{r.p!.name}</span>
                {/* лист «товар в кластере» (группировка «Канал: Регион → Товар») */}
                {r.l && <PaidStorageBadge paid={r.paid} />}
                {loss && (
                  <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                    убыток
                  </span>
                )}
                {/* ⚠️ «На вывод» = НЕ ПРОИЗВОДИМ, а не «не возим»: остатки надо довезти и
                    распродать, иначе они лягут на складе. Флаг живёт в 1С и на экране не был виден
                    ничем, кроме слова «ВЫВОД», вписанного в название руками — а без него причина
                    странного поведения товара необъяснима. */}
                {r.p!.delisting && (
                  <span
                    className="shrink-0 rounded bg-violet-100 px-1 text-[10px] font-medium text-violet-700 dark:bg-violet-950 dark:text-violet-300"
                    title="«На вывод» в 1С: не производим. Возить и распродавать остатки надо — товар участвует в распределении как обычный, но в план производства не идёт."
                  >
                    на вывод
                  </span>
                )}
                {r.p!.noMovement && (
                  <span
                    className="shrink-0 rounded bg-slate-200 px-1 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    title="Ни заказов, ни остатка, ни поставок в пути ни на одной площадке — новинка или забытая карточка; планировать можно вручную"
                  >
                    нет движения
                  </span>
                )}
                {noSku.map((c) => (
                  <span
                    key={c}
                    className="shrink-0 rounded bg-orange-100 px-1 text-[10px] font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-300"
                    title={`В спецификации 1С канал «${CH_LABEL[c]}» есть, а мост на карточку маркетплейса не заполнен (доп. реквизит ${c.toUpperCase()}_SKU у номенклатуры). Планировать туда нечем: поставка ушла бы в файл без артикула и без юрлица. Чинится в 1С.`}
                  >
                    нет артикула {CH_LABEL[c]}
                  </span>
                ))}
                {mute && (
                  <span
                    className="shrink-0 rounded bg-slate-200 px-1 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    title="У каналов этого товара не осталось ни одной включённой ВАМИ цели — возить некуда. Включить: «Куда планируем»."
                  >
                    некуда возить
                  </span>
                )}
                {SUPPLY_UI.assembly && (
                  <AssemblyBlockBadge block={r.p!.block} />
                )}
                {offBadge}
              </span>
            );
          }
          if (r.dim === "channel")
            return (
              <span className="flex items-center gap-1 truncate pl-4 font-medium">
                {chevron}
                {badge}
                <span className="text-muted-foreground">· {r.nKids} рег.</span>
              </span>
            );
          // составной «канал: регион» и обычный регион-лист
          return (
            <span
              className={cn(
                "flex items-center gap-1.5 truncate",
                r.dim === "region" ? "pl-10" : "pl-4",
              )}
            >
              {chevron}
              {r.dim === "chregion" && badge}
              <span className="truncate text-muted-foreground">{r.region}</span>
              <PaidStorageBadge paid={r.paid} />
              {r.l?.disabled && <AssemblyBlockBadge block="target_off" />}
            </span>
          );
        },
      },
      num("orders", "Заказы"),
      num("stock", "Остаток"),
      num("supply", "В пути"),
      // считается только на регионе (по его истории остатков) — на агрегатах смысла не имеет.
      // Справочная: в скорость с 15.10.2026 не входит.
      num("days_avail", "Дней в нал.", ({ row }) =>
        row.original.l ? (
          <span title="Справочно: дней с остатком выше 95-го перцентиля дневных заказов. В скорость не входит.">
            {int0(row.original.l.days_avail)}
          </span>
        ) : null,
      ),
      num("speed", "Скорость", ({ row, getValue }) => {
        const l = row.original.l;
        if (!l) return dec1(getValue<number>());
        // на регионе видно, какое окно взято: max(7 дн, 30 дн). У листа, склеенного из двух позиций
        // МП, скорость = сумма max-ов и может не совпасть ни с одним окном — тогда метку не ставим.
        const took7 = l.speed_7d > l.speed_30d;
        const tagged =
          l.speed > 0 &&
          Math.abs(l.speed - Math.max(l.speed_7d, l.speed_30d)) < 0.002;
        return (
          <span
            title={`7 дн: ${dec1(l.speed_7d)} · 30 дн: ${dec1(l.speed_30d)} шт/дн — взято большее`}
          >
            {dec1(getValue<number>())}
            {tagged && (
              <span className="ml-1 text-[10px] text-muted-foreground">
                {took7 ? "7д" : "30д"}
              </span>
            )}
          </span>
        );
      }),
      depth("depth", "Глубина"),
      depth("future_depth", "Будущая глубина", true),
      // пул готово/доупак — на товар (общий между каналами), на канале и регионе его нет
      // пул готово/доупак — на ТОВАР: на строке канала или региона он был бы бессмыслен
      num("ready", "Остаток ГП", ({ row }) =>
        row.original.dim === "product" && row.original.p
          ? ctxRef.current.hasCap
            ? int0(row.original.p.ready)
            : "…"
          : null,
      ),
      num("assemble", "Доупак", ({ row }) => {
        const r = row.original;
        if (r.dim !== "product" || !r.p) return null;
        const b = blockInfo(r.p);
        return (
          <span className={b?.cellCls} title={b?.title}>
            {!ctxRef.current.hasCap
              ? "…"
              : (b?.cell ?? ctxRef.current.doupak(r.p.assemble))}
          </span>
        );
      }),
      // шт. в коробе: квант отгрузки, из-за него «Решение» округляется вниз
      num("in_box", "Квант", ({ row }) =>
        row.original.l && row.original.l.in_box > 0
          ? int0(row.original.l.in_box)
          : null,
      ),
      num("need", "Допоставить"),
      {
        ...num("decision", "Решение"),
        cell: ({ row }) => (
          <DecisionCell row={row.original} ctx={ctxRef.current} />
        ),
      },
      // «Готово»/«Доупак» скрываются целиком (см. `features.ts`), поэтому и колонок нет:
      // прятать их видимостью нельзя — пользователь включил бы их обратно в меню «Колонки»
      num("boxes", "Коробов", ({ row }) => {
        const r = row.original;
        const c = ctxRef.current;
        if (r.l)
          return r.l.in_box > 0 ? dec1(liveDecision(r) / r.l.in_box) : "—";
        if (r.dim !== "product" || !r.p) return null;
        // на товаре здесь полезнее остаток пула, чем короба: сколько ещё можно отгрузить.
        // ⚠️ В режиме полки доупака НЕТ — прибавлять его сюда значит показывать остаток, которого
        // не существует, ровно там, где по нему и решают, сколько ещё можно увезти
        const remain =
          r.p.ready + (c.shelf ? 0 : r.p.assemble) - liveDecision(r);
        if (!c.engineReady || !c.hasCap) return "";
        return (
          <span
            className={cn(
              remain < 0
                ? "text-red-600 dark:text-red-400"
                : "text-muted-foreground",
            )}
            title={
              c.shelf
                ? `Остаток ГП после решения: ${int0(r.p.ready)} − ${int0(liveDecision(r))} = ${int0(remain)} шт`
                : `Остаток пула (готово + доупак) после решения: ${int0(remain)} шт`
            }
          >
            {int0(remain)} ост.
          </span>
        );
      }),
    ];
    // прячем ТОЛЬКО «Доупак»: «Остаток ГП» — живой остаток склада, к флаконной модели не относится.
    // Прятать видимостью колонки нельзя — пользователь включил бы её обратно в меню «Колонки».
    return cols.filter((c) => SUPPLY_UI.assembly || c.id !== "assemble");
  }, []);

  const table = useReactTable({
    data: tree,
    columns,
    getRowId: (r) => r.key,
    getSubRows: (r) => (r.l ? undefined : r.subRows),
    // subRows у нераскрытых пусты (см. сборку дерева), поэтому раскрываемость объявляем явно:
    // раскрывается всё, что не лист (лист = строка с конкретным регионом одного товара)
    getRowCanExpand: (row) => (row.original.nKids ?? 0) > 0,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded: tableExpanded,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setTableExpanded,
    getCoreRowModel: getCoreRowModel(),
    // сортировка идёт ПО КАЖДОМУ УРОВНЮ отдельно — колонка упорядочивает то, что видно на
    // этом уровне, каким бы измерением он ни оказался в текущем режиме группировки
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // фильтры применяем САМИ (при сборке дерева): нужно пересобрать агрегаты товара/канала по
    // выжившим листьям, чего строковая модель библиотеки не делает — она только прячет строки
    manualFiltering: true,
    enableSortingRemoval: true,
  });
  const rows = table.getRowModel().rows;
  const visibleCols = table.getVisibleLeafColumns();

  // ── итоги ──
  const totals = useMemo(() => {
    let orders = 0,
      stock = 0,
      supply = 0,
      need = 0,
      ready = 0,
      assemble = 0,
      decision = 0;
    // ⚠️ «Решение» в ИТОГО раскладываем ПО КАНАЛАМ и по тому, откуда взято число. Пока это была
    // одна цифра, в ней нельзя было отличить «мы это решили» от «портал подставил потребность,
    // потому что движок не запускали», и итог читался как чужие решения из выключенных каналов.
    const byChannel: Record<string, number> = { wb: 0, ozon: 0, ym: 0 };
    let saved = 0;
    for (const p of filtered) {
      orders += p.orders;
      stock += p.stock;
      supply += p.supply;
      need += p.need;
      ready += p.ready;
      assemble += p.assemble;
      for (const c of p.channels)
        for (const l of c.regions) {
          const v = decisionOf(p.nom_key, c.channel, l);
          decision += v;
          byChannel[c.channel] = (byChannel[c.channel] ?? 0) + v;
          if (decisionSourceOf(p.nom_key, c.channel, l) !== "need") saved += v;
        }
    }
    return {
      orders,
      stock,
      supply,
      need,
      ready,
      assemble,
      decision,
      byChannel,
      saved,
      count: filtered.length,
    };
  }, [filtered, decisionOf, decisionSourceOf]);

  // Предупреждения: несопоставленные регионы (→ «Не известен») + прочие warnings отчётов — не
  // глотаем. Но ТОЛЬКО по каналам, куда сейчас возим: у выключенного МП несостыковки чинить нечего,
  // а блок из-за них висел постоянно и приучал на себя не смотреть. Пусто → блока нет вовсе.
  const dataWarnings = useMemo(() => {
    const w: string[] = [];
    for (const [code, label, rep] of [
      ["wb", "WB", wbData],
      ["ozon", "Ozon", oz.data],
      ["ym", "YM", ym.data],
    ] as const) {
      if (!rep || liveTargets[code].length === 0) continue;
      const un = [
        ...(rep.unmatched_okrugs ?? []),
        ...(rep.unmatched_warehouses ?? []),
      ];
      if (un.length)
        w.push(
          `${label}: не сопоставлены регионы (${un.length}) → «Не известен»: ${un.slice(0, 6).join(", ")}${un.length > 6 ? "…" : ""}`,
        );
      for (const x of rep.warnings ?? []) w.push(`${label}: ${x}`);
    }
    // один mp_sku без номенклатуры приходит строкой на КАЖДЫЙ регион — считаем позиции, а не строки
    const lost = [
      ...new Map(
        dropped
          .filter((d) => liveTargets[d.channel].length > 0)
          .map((d) => [d.label, d]),
      ).values(),
    ];
    if (lost.length)
      w.push(
        `Без номенклатуры 1С: ${lost.length} позиций МП не попали в таблицу — ${lost
          .slice(0, 5)
          .map((d) => d.label)
          .join(", ")}${lost.length > 5 ? "…" : ""}`,
      );
    // как и `dropped`: счётчик + первые примеры. У товара с несколькими mp_sku таких пар бывают
    // сотни, и баннер из сотен строк читать никто не станет
    const dbl = merged.filter((m) => liveTargets[m.channel].length > 0);
    if (dbl.length)
      w.push(
        `Две позиции МП на одну номенклатуру: ${dbl.length} случ. — числа сложены. ${dbl
          .slice(0, 3)
          .map((m) => m.text)
          .join("; ")}${dbl.length > 3 ? "…" : ""}`,
      );
    // товар без движения, у которого нет sku ни на одном канале спеки: строк у него нет, и бейджу
    // «нет артикула» стоять негде — без этой строки он пропал бы с экрана молча
    if (seededNoSku.length)
      w.push(
        `Без движения и без артикула МП: ${seededNoSku.length} товаров не попали в таблицу — в спеке 1С канал есть, а мост sku у номенклатуры не заполнен: ${seededNoSku
          .slice(0, 5)
          .map((x) => `${x.label} (${x.noSku.map((c) => CH_LABEL[c]).join(", ")})`)
          .join(", ")}${seededNoSku.length > 5 ? "…" : ""}`,
      );
    if (failedDemand.length)
      w.push(
        `Товары без движения не показаны: не загрузился отчёт ${failedDemand.join(", ")}`,
      );
    return w;
  }, [
    wbData,
    oz.data,
    ym.data,
    dropped,
    merged,
    seededNoSku,
    liveTargets,
    failedDemand,
  ]);
  // несопоставленные регионы (структурировано, для модалки-редактора): канал + тип + значение + цели
  const [mapOpen, setMapOpen] = useState(false);
  const unmatchedItems = useMemo(() => {
    const items: UnmatchedItem[] = [];
    for (const [mp_code, mp_label, rep] of [
      ["wb", "WB", wbData],
      ["ozon", "Ozon", oz.data],
      ["ym", "YM", ym.data],
    ] as const) {
      if (!rep || liveTargets[mp_code].length === 0) continue; // выключенный канал нечего сопоставлять
      const regions = rep.all_regions ?? [];
      for (const v of rep.unmatched_okrugs ?? [])
        items.push({
          mp_code,
          mp_label,
          source_type: "okrug",
          source_value: v,
          regions,
        });
      for (const v of rep.unmatched_warehouses ?? [])
        items.push({
          mp_code,
          mp_label,
          source_type: "warehouse",
          source_value: v,
          regions,
        });
    }
    return items;
  }, [wbData, oz.data, ym.data, liveTargets]);
  // Строка ИТОГО по id колонки (не по позиции: колонки прячутся и переставляются). Считаем по
  // ОТФИЛЬТРОВАННЫМ товарам, поэтому она всегда согласована с тем, что видно.
  const totalSpeed = filtered.reduce(
    (a, p) =>
      a +
      p.channels.reduce(
        (b, c) => b + c.regions.reduce((d, l) => d + l.speed, 0),
        0,
      ),
    0,
  );
  const totalCells: Record<string, React.ReactNode> = {
    article: "ИТОГО",
    name: `${filtered.length} тов.`,
    orders: int0(totals.orders),
    stock: int0(totals.stock),
    supply: int0(totals.supply),
    speed: dec1(totalSpeed),
    depth: (
      <span
        className={depthClass(
          totalSpeed > 0 ? totals.stock / totalSpeed : DEPTH_INF,
        )}
      >
        {depthFmt(totalSpeed > 0 ? totals.stock / totalSpeed : DEPTH_INF)}
      </span>
    ),
    future_depth: (() => {
      const d = futureDepth(
        totals.stock,
        totals.supply,
        totals.decision,
        totalSpeed,
      );
      return <span className={depthClass(d)}>{depthFmt(d)}</span>;
    })(),
    ready: hasCap ? int0(totals.ready) : "—",
    assemble: hasCap ? doupak(totals.assemble) : "—",
    need: int0(totals.need),
    decision: <TotalDecision totals={totals} />,
  };

  // ── ширины колонок (ресайз через CSS-переменные, без ре-рендера мид-драга) ──
  const visibleIds = useMemo(() => visibleCols.map((c) => c.id), [visibleCols]);
  const { gridRef, gridTemplate, vars, startResize } = useColumnResize(
    FRS_STORAGE,
    DEFAULT_FRS,
    visibleIds,
  );
  /** Объём фиксации ОДНИМ расчётом на кнопку, подсказку и подтверждение (`hideMine.ts`):
   *  уедет ли всё и что при этом спрятано лично. Три отдельные правды на этих трёх экранах уже
   *  расходились: скрытый регион при тех же товарах не менял ни счётчик, ни confirm. */
  const fix = fixationScope({
    hidden,
    shown: filtered.length,
    total: products.length,
    filters: columnFilters.length,
  });

  /** Собрать «Решения» и зафиксировать. `only` — набор товаров (частичная фиксация) или null
   *  (все). Частичная безопасна: числа посчитаны в ПОЛНОЙ конкуренции за общий флакон, мы просто
   *  отгружаем часть сейчас, а остальные решения остаются сохранёнными до следующего захода. */
  /** Что уедет в поставки.
   *  - `shown` — то, что СЕЙЧАС в таблице: товары и строки, прошедшие фильтры колонок. Раньше
   *    кнопка брала весь список независимо от фильтра, и менеджер, отфильтровавший свой
   *    маркетплейс, одним кликом отправлял заодно чужие. Теперь «что видно, то и уедет».
   *  - `selected` — отмеченные галочками И видимые (`selShown`). Галочка, спрятанная фильтром,
   *    в отгрузку НЕ идёт: менеджер, выделивший строки и потом сузивший таблицу, видел 3 позиции,
   *    а кнопка увозила все 14. Выделение при этом цело — снимут фильтр, вернётся в действие.
   *    Оба режима ходят по `filtered`, поэтому и строки (канал × регион) берутся только видимые. */
  const fixScope = (mode: "shown" | "selected"): void => {
    const only = mode === "selected";
    const lines: api.FixLine[] = [];
    const noms = new Set<string>();
    for (const p of filtered) {
      if (only && !selShown.has(p.nom_key)) continue;
      for (const ch of p.channels)
        for (const l of ch.regions) {
          // отдельный гард на `disabled` не нужен: `decisionOf` даёт выключенной цели 0, и её
          // отсекает `q > 0`. Правило живёт в ОДНОМ месте, а бэкенд ловит его же на своей стороне.
          const q = decisionOf(p.nom_key, ch.channel, l);
          if (q > 0) {
            lines.push({
              nom_key: p.nom_key,
              channel: ch.channel,
              region: l.region,
              qty: q,
            });
            noms.add(p.nom_key);
          }
        }
    }
    if (lines.length === 0) {
      toast.error(
        only
          ? "У выбранных ПОКАЗАННЫХ товаров нет «Решений» (qty>0)"
          : "Нет «Решений» (qty>0) — проставьте количества в показанных строках",
      );
      return;
    }
    // подтверждение называет объём словами: числа строк мало, чтобы понять, ЧТО именно уедет.
    // Фильтр колонки может отсечь СТРОКИ, не тронув ни одного товара («Допоставить > 0» и т.п.),
    // а «Скрыть у себя» — целые регионы при тех же товарах: и там, и там товаров столько же, а
    // уедет меньше, и «ВСЕ товары» было бы неправдой. Оба случая считает `fixationScope`.
    const scope = only
      ? `выбранные показанные товары (${noms.size})${
          selHidden > 0
            ? `; ещё ${selHidden} выделенных скрыто (фильтр / «Скрыть у себя») и НЕ уедет`
            : ""
        }`
      : fix.partial
        ? `показанные товары (${noms.size} из ${products.length})`
        : `ВСЕ товары (${noms.size})`;
    const tail = SUPPLY_UI.poolReserve ? " + бронь пула" : "";
    // скрытое называем и в «выбранном»: спрятанный регион ВИДИМОГО товара выпадает так же молча
    const hiddenTail = fix.hiddenNote ? `; ${fix.hiddenNote}` : "";
    if (
      !window.confirm(
        `Зафиксировать ${scope}${hiddenTail}: ${lines.length} строк → поставки по регионам${tail}?`,
      )
    )
      return;
    // след в поставке — тот же: через месяц «почему этого региона нет» разбирают по заметке run'а
    const hiddenMark = fix.hiddenNote ? ` (${fix.hiddenNote})` : "";
    fixMut.mutate({
      lines,
      note: only
        ? `только выбранные показанные: ${noms.size} тов.${hiddenMark}`
        : fix.partial
          ? `только показанные: ${noms.size} тов.${hiddenMark}`
          : undefined,
    });
  };

  const refreshAll = (): void => {
    for (const k of Object.values(KEYS)) qc.invalidateQueries({ queryKey: k });
  };

  const loading = wb.isLoading || oz.isLoading || ym.isLoading;
  // Ошибки отчётов спроса по каналам. WB (в т.ч. 503 витрины dwh) подробно — ещё и в плашке
  // источника WB, но и здесь: его строк в таблице нет, и причина должна стоять рядом с ней.
  const errs = (
    [
      ["WB", wb.error],
      ["Ozon", oz.error],
      ["YM", ym.error],
    ] as const
  ).filter(([, e]) => e != null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">
          Распределение поставок — WB + Ozon + Яндекс
        </h2>
        {anyFetching && (
          <span className="flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-400">
            <RefreshCw className="size-3 animate-spin" /> спрос…
          </span>
        )}
        {capFetching && (
          <span className="flex items-center gap-1 rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-950 dark:text-sky-400">
            <RefreshCw className="size-3 animate-spin" />{" "}
            {SUPPLY_UI.assembly ? "готово/доупак из 1С…" : "остаток ГП из 1С…"}
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowHelp((s) => !s)}
          className="ml-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <Info className="size-4" /> Как считается
          <ChevronDown
            className={cn(
              "size-3.5 transition-transform",
              showHelp && "rotate-180",
            )}
          />
        </button>
      </div>

      {showHelp && (
        <div className="grid gap-4 rounded-md border bg-muted/30 px-4 py-3 text-sm text-muted-foreground md:grid-cols-2">
          <div className="space-y-1.5">
            <div className="font-medium text-foreground">Структура</div>
            <p>
              Дерево:{" "}
              <b className="text-foreground">
                Товар → Канал (WB / Ozon / Яндекс.Маркет) → Регион / кластер /
                склад
              </b>
              .
            </p>
            {SUPPLY_UI.assembly ? (
              <p>
                <b className="text-foreground">Готово + Доупак</b> — общий пул
                на товар между каналами.
                <b className="text-foreground"> Решение</b> (реальная отгрузка)
                редактируется по регионам, предзаполнено из «Допоставить». Сумма
                решений по товару больше пула →{" "}
                <span className="font-medium text-red-600 dark:text-red-400">
                  красный дефицит
                </span>
                .
              </p>
            ) : (
              <p>
                <b className="text-foreground">Решение</b> — то, что реально
                уедет. Редактируется на строке региона, предзаполнено расчётным
                «Допоставить». Строки есть на все включённые цели, в том числе с
                нулями: туда можно завезти товар, которого там ещё не было.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <div className="font-medium text-foreground">
              Как считается «Допоставить»
            </div>
            <div className="rounded border bg-background px-3 py-1.5 font-mono text-xs text-foreground">
              (ГТЗ − текущая&nbsp;глубина) × скорость&nbsp;→&nbsp;коробов
            </div>
            <p>
              Скорость, шт/дн — большее из двух: заказы за последние 7 дней ÷ 7
              и заказы за 30 дней ÷ 30 (метка «7д»/«30д» у числа — какое взято).
              Делим на календарные дни, дни без остатка скорость не поднимают; «Дней в нал.» — справочно.
              Потребность обнуляется, если:
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              <li>
                будущая глубина превысит{" "}
                <b className="text-foreground">{params.overstock}</b> дн{" "}
                <span className="text-muted-foreground/70">(потолок)</span>;
              </li>
              <li>
                общее покрытие SKU выше{" "}
                <b className="text-foreground">{params.catalog}</b> дн{" "}
                <span className="text-muted-foreground/70">(порог)</span> и
                позиция не ходовая (заказы&nbsp;&lt;&nbsp;3) или локальная
                глубина ≥ ГТЗ;
              </li>
              <li>
                локальная глубина уже ≥ ГТЗ (
                <b className="text-foreground">{params.target}</b> дн).
              </li>
            </ul>
            <p>
              <b className="text-foreground">Итог</b> = потребность − «в пути».
            </p>
          </div>
        </div>
      )}

      <WbSourceBar
        report={wbData}
        error={wb.error}
        loading={wb.isLoading}
        fetching={wb.isFetching}
        params={params}
      />
      {allocWbMismatch && alloc.data && wbData && (
        <p
          data-testid="alloc-wb-mismatch"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-destructive dark:border-red-900 dark:bg-red-950/40"
        >
          Движок и таблица WB посчитаны по разным источникам: движок —{" "}
          {wbSourceLabel(alloc.data.wb_source)}, таблица —{" "}
          {wbSourceLabel(wbData.wb_source)}
          {wb.isFetching || alloc.isFetching ? "; пересчитываю…" : ""}
        </p>
      )}
      {engineOn && (alloc.data?.wb_warnings.length ?? 0) > 0 && (
        <div
          data-testid="alloc-wb-warnings"
          className="space-y-0.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
        >
          {alloc.data?.wb_warnings.map((w, i) => (
            <div key={i}>• Движок, спрос WB: {w}</div>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border bg-card py-16 text-center">
          <RefreshCw className="size-7 animate-spin text-muted-foreground" />
          <p className="text-sm font-medium">
            Считаю спрос WB + Ozon + Яндекс…
          </p>
        </div>
      )}
      {errs.map(([label, e]) => (
        <p key={label} className="text-sm text-destructive">
          Отчёт {label} не получен: {extractErrorMessage(e, "Ошибка загрузки")}
        </p>
      ))}
      {cards.error && (
        <p className="text-sm text-destructive">
          Карточки МП из 1С не загрузились: товары без движения, бейджи «нет
          артикула» и квант из спеки не показаны.{" "}
          {extractErrorMessage(cards.error, "Ошибка загрузки")}
        </p>
      )}
      {/* Холодный ответ 1С — ~30 с: без строки товары без движения появлялись бы молча, позже */}
      {cards.isLoading && (
        <p className="text-sm text-muted-foreground">
          Товары без движения загружаются из 1С (карточки МП, до ~30 с)…
        </p>
      )}

      {!loading && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border bg-card px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            товаров <b className="text-foreground">{filtered.length}</b>
          </span>
          {/* компактные отсечки calc_supply (дней) */}
          <div className="flex items-center gap-2 rounded-md border px-2 py-1">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              Отсечки, дн:
              <Hint>
                Правила, по которым считается «Допоставить». Меняются на лету —
                спрос пересчитывается заново (это около минуты). Сохраняются
                лично за вами: у соседа могут быть свои.
              </Hint>
            </span>
            <MiniNum
              label="ГТЗ"
              value={draft.target}
              onChange={(v) => setDraft((d) => ({ ...d, target: v }))}
              hint={
                <>
                  <b>Гарантированный товарный запас</b> — на сколько дней продаж
                  везём.
                  <br />
                  Допоставить = ГТЗ × скорость − остаток − то, что уже{" "}
                  <b>в пути</b>. После поставки запаса в регионе будет примерно
                  на {params.target} дн — это и показывает колонка «Будущая
                  глубина».
                  <br />
                  Больше — реже возим, но больше денег заморожено в товаре.
                </>
              }
            />
            <MiniNum
              label="Потолок"
              value={draft.overstock}
              onChange={(v) => setDraft((d) => ({ ...d, overstock: v }))}
              hint={
                <>
                  <b>Страховка от перетарки.</b> Если после поставки запаса в
                  регионе станет больше {params.overstock} дн — не везём туда
                  вообще (ставим 0).
                  <br />
                  Срабатывает в основном на медленных позициях, где даже один
                  короб — это запас на годы.
                </>
              }
            />
            <MiniNum
              label="Порог"
              value={draft.catalog}
              onChange={(v) => setDraft((d) => ({ ...d, catalog: v }))}
              hint={
                <>
                  <b>Когда товара и так много ВЕЗДЕ.</b> Смотрит на запас
                  позиции целиком по всем регионам: если он больше{" "}
                  {params.catalog} дн, довозим только ходовое — от 3 заказов за
                  30 дней и при запасе в самом регионе ниже ГТЗ. Остальным 0.
                  <br />
                  Это про «не размазывать по складам то, что и так лежит».
                </>
              }
            />
            {pendingParams && (
              <RefreshCw className="size-3 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {SUPPLY_UI.topFilters && (
              <Input
                placeholder="Поиск: артикул / товар…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-52"
              />
            )}
            {/* те же фильтры есть в колонках таблицы; две панели рядом только путают */}
            {SUPPLY_UI.topFilters && (
              <>
                <RegionMultiSelect
                  all={CHANNELS}
                  labelOf={(c) => CH_LABEL[c as Channel]}
                  selected={selCh}
                  onChange={setSelCh}
                  allLabel="Все каналы"
                  countWord="Каналов"
                />
                <RegionMultiSelect
                  groups={regionGroups}
                  selected={selReg}
                  onChange={setSelReg}
                  allLabel="Все регионы"
                  countWord="Регионов"
                />
              </>
            )}
            <ToolbarMenu
              label="Группировка:"
              value={GROUP_MODES.find(([m]) => m === groupMode)?.[1]}
              width="w-56"
              title="Порядок вложенности строк. Числа, фильтры и фиксация от него не зависят — меняется только группировка."
            >
              {(close) =>
                GROUP_MODES.map(([m, lbl]) => (
                  <MenuItem
                    key={m}
                    active={groupMode === m}
                    onClick={() => {
                      switchGroup(m);
                      close();
                    }}
                  >
                    {lbl}
                  </MenuItem>
                ))
              }
            </ToolbarMenu>
            <ToolbarMenu
              label="Раскрыть до"
              width="w-44"
              title="Развернуть дерево до нужного уровня целиком"
            >
              {(close) =>
                LEVELS[groupMode].map((lv, i) => (
                  <MenuItem
                    key={lv.dim}
                    onClick={() => {
                      expandTo(i);
                      close();
                    }}
                  >
                    {lv.label}
                  </MenuItem>
                ))
              }
            </ToolbarMenu>
            <ColumnsMenu table={table} />
            <PlanningTargets
              regions={settingsRegions}
              off={off}
              isOff={isOff}
              onToggle={targetMut.mutate}
              busy={targetMut.isPending}
              showOff={showOff}
              onShowOff={setShowOff}
            />
            {/* Личный фильтр обзора — ВПЛОТНУЮ к общей настройке: выбор между «не возить туда
                всей командой» и «убрать с моего экрана» человек делает в одном месте. */}
            <DistributionHidePicker
              channels={hideChannels}
              hidden={hidden}
              onChange={setHidden}
            />
            {/* Юрлицо не видно нигде до фиксации, а поставку делают сначала по одному, потом по
                второму. «Зафиксировать показанное» берёт РОВНО отфильтрованное — значит фильтр и
                есть способ это сделать. Пустое значение («не определилось») выбирается наравне:
                прятать такие позиции нельзя, по ним 1С-заказ всё равно заблокируется. */}
            {legalOptions.length > 1 ? (
              <RegionMultiSelect
                all={legalOptions}
                labelOf={(v) => v || "юрлицо не определилось"}
                selected={selLegal}
                onChange={setSelLegal}
                allLabel="Все юрлица"
                countWord="Юрлиц"
              />
            ) : (
              // ⚠️ Молча исчезнуть фильтр не имеет права: «его нет» и «карта юрлиц не загрузилась»
              // выглядели одинаково, и человек искал кнопку, которой не было по другой причине
              <Button size="sm" variant="outline" disabled title={legalHint}>
                {legals.isFetching
                  ? "Юрлица…"
                  : legals.isError
                    ? "Юрлица не загрузились"
                    : "Все юрлица"}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={clearMut.isPending || (dec.data?.length ?? 0) === 0}
              title="Удалить сохранённые «Решения» — таблица вернётся к рекомендации движка / потребности"
              onClick={() => {
                const n = dec.data?.length ?? 0;
                if (
                  window.confirm(
                    `Удалить сохранённые «Решения» (${n} шт)? Ручные правки будут сброшены, останется рекомендация движка.`,
                  )
                ) {
                  setEdited(new Map());
                  clearMut.mutate();
                }
              }}
            >
              <Trash2
                className={cn("size-4", clearMut.isPending && "animate-pulse")}
              />
              Очистить решения
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={refreshAll}
              disabled={anyFetching || capFetching}
            >
              <RefreshCw
                className={cn(
                  "size-4",
                  (anyFetching || capFetching) && "animate-spin",
                )}
              />
              Обновить
            </Button>
            {/* ⚠️ Кнопка, а не автомат: «Решение» по умолчанию показывает ИДЕАЛ («сколько надо
                довезти»), и переход к «сколько реально уедет» человек делает осознанно. Стоит
                вплотную к фиксации, потому что это один порядок действий: сначала разделить
                остаток, потом зафиксировать показанное. В середине панели, среди меню вида, её
                просто не находили. */}
            <Button
              size="sm"
              variant={shelf ? "default" : "outline"}
              disabled={alloc.isFetching}
              title={
                shelf
                  ? "«Решение» ограничено остатком ГП: регионы добиты до ГТЗ в порядке количества заказов. Нажмите ещё раз, чтобы вернуться к расчётной потребности"
                  : "Разделить физический остаток ГП по выбранным каналам и регионам: добиваем каждый регион до ГТЗ в порядке количества заказов — кто больше продаёт, тот забирает остаток первым. Доупак не участвует."
              }
              onClick={() => setShelf((v) => !v)}
            >
              <Boxes
                className={cn(
                  "size-4",
                  alloc.isFetching && shelf && "animate-pulse",
                )}
              />
              {shelf ? "Считаем по остатку ГП" : "Распределить по остатку ГП"}
            </Button>
            {/* Число на кнопке — ровно то, что уедет: выделенные ВИДИМЫЕ. Когда фильтр спрятал
                часть галочек, об этом написано в title и в панели выбора, а не выяснится после
                клика. Все галочки спрятаны — кнопку не убираем, а гасим: исчезнувшая кнопка
                читается как «выбор потерялся». */}
            {selLive.size > 0 && (
              <Button
                size="sm"
                onClick={() => fixScope("selected")}
                disabled={fixMut.isPending || selShown.size === 0}
                title={
                  selShown.size === 0
                    ? `Все ${selLive.size} выделенных скрыты (фильтр / «Скрыть у себя») — снимите, чтобы их зафиксировать`
                    : selHidden > 0
                      ? `Уедут только выделенные, видимые сейчас: ${selShown.size} тов. Ещё ${selHidden} выделенных скрыто (фильтр / «Скрыть у себя») и не уедет`
                      : fix.hiddenNote
                        ? // товар видим, а часть его регионов спрятана: они выпадают так же молча
                          `Текущие «Решения» по выделенным товарам → поставки; ${fix.hiddenNote}`
                        : "Текущие «Решения» по выделенным товарам → поставки (вкладка «Отгрузки»)"
                }
              >
                <RefreshCw
                  className={cn("size-4", fixMut.isPending && "animate-spin")}
                />
                Зафиксировать выбранное ({selShown.size})
              </Button>
            )}
            {/* Кнопка называет РОВНО то, что сделает. «Потребность» — только когда уедет всё:
                скрытое у себя тоже сужает отгрузку (строки выпадают из дерева до сборки
                «Решений»), и при спрятанном регионе «Зафиксировать потребность» с прежним числом
                товаров было прямой неправдой. */}
            <Button
              size="sm"
              variant={selShown.size > 0 ? "outline" : "default"}
              onClick={() => fixScope("shown")}
              disabled={fixMut.isPending}
              title={
                fix.partial
                  ? `Уедет только то, что сейчас в таблице: ${filtered.length} тов. из ${products.length}, строки с «Решением» больше нуля${
                      fix.hiddenNote ? `; ${fix.hiddenNote}` : ""
                    }`
                  : "Текущие «Решения» по всем показанным товарам → поставки (вкладка «Отгрузки»)"
              }
            >
              <RefreshCw
                className={cn("size-4", fixMut.isPending && "animate-spin")}
              />
              {fix.partial
                ? `Зафиксировать показанное (${filtered.length})`
                : `Зафиксировать потребность (${products.length})`}
            </Button>
          </div>
        </div>
      )}

      {!loading && (selLive.size > 0 || priorityNoms.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
          <span className="font-medium">
            Выбрано товаров: <b>{selShown.size}</b>
            {selHidden > 0 && (
              // разница между галочками и объёмом действия — НА ЭКРАНЕ: действие берёт только
              // видимых, и «куда делись остальные» не должно выясняться после клика
              <span className="ml-1.5 font-normal text-amber-700 dark:text-amber-400">
                · скрыто (фильтр / «Скрыть у себя»): {selHidden}
              </span>
            )}
            {priorityNoms.length > 0 && (
              // применённый выбор влияет на движок, даже если галки уже сняты — показываем его
              // явно, иначе он «залипал» бы в расчёте без единого элемента управления
              <span className="ml-1.5 font-normal text-muted-foreground">
                · в расчёте: {priorityNoms.length}
              </span>
            )}
          </span>
          <span className="text-xs text-muted-foreground">
            {!SUPPLY_UI.engine
              ? "«Зафиксировать выбранное» отгрузит только видимых из них; решения по остальным останутся"
              : !engineOn
                ? "включите движок, чтобы распределить пул на выбранные в первую очередь"
                : selDirty
                  ? "выбор не применён — движок пересчитывается ~19 с, поэтому по кнопке"
                  : "движок отдаёт им дефицитный пул первыми; остальные считаются как обычно — из остатка"}
          </span>
          {SUPPLY_UI.engine && engineOn && selDirty && (
            <Button
              size="sm"
              onClick={() => setPriorityNoms([...selShown].sort())}
              disabled={alloc.isFetching}
            >
              <Sparkles
                className={cn("size-4", alloc.isFetching && "animate-pulse")}
              />
              Распределить выбранное
            </Button>
          )}
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={onlySelected}
              onChange={(e) => setOnlySelected(e.target.checked)}
            />
            показывать только выбранные
          </label>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() => {
              setSel(new Set());
              setPriorityNoms([]);
              setOnlySelected(false);
            }}
          >
            <X className="size-4" /> Сбросить выбор
          </Button>
        </div>
      )}

      {SUPPLY_UI.engine && !loading && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border bg-card px-3 py-2 text-sm">
          <span className="flex items-center gap-1.5 font-medium">
            <Sparkles className="size-4 text-primary" /> Рекомендация движка
          </span>
          <div className="flex items-center gap-0.5 rounded-md border p-0.5">
            {(
              [
                ["off", "Выкл"],
                ["protect_then_margin", "Защита + маржа"],
                ["pure_margin", "Чистая маржа"],
              ] as [EngineMode, string][]
            ).map(([m, lbl]) => (
              <button
                key={m}
                type="button"
                onClick={() => setEngine(m)}
                className={cn(
                  "rounded px-2 py-1 text-xs font-medium transition-colors",
                  engine === m
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {lbl}
              </button>
            ))}
          </div>
          {engineOn && (
            <div className="flex items-center gap-2 rounded-md border px-2 py-1">
              <MiniNum
                label="Защита, дн"
                hint={
                  <>
                    <b>Защита от пустой полки.</b> Когда товара на всех не
                    хватает, движок сначала доводит КАЖДЫЙ регион до {floorDays}{" "}
                    дн запаса и только потом раздаёт остаток по марже.
                    <br />0 = сразу отдавать самым прибыльным, пустые регионы
                    подождут.
                  </>
                }
                value={floorDays}
                onChange={(v) =>
                  setFloorDays(
                    Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0,
                  )
                }
              />
              <MiniNum
                label="Упаковка, очков/дн"
                hint={
                  <>
                    <b>Мощность упаковки</b> — сколько единиц в день реально
                    успеваем собрать. Одиночка стоит 1 очко, набор 2: он вдвое
                    медленнее.
                    <br />
                    Умноженное на горизонт в 14 дней, это общий потолок
                    «Доупака» на все товары сразу — движок не пообещает больше,
                    чем цех успеет собрать.
                  </>
                }
                value={packPerDay}
                onChange={(v) =>
                  setPackPerDay(
                    Number.isFinite(v) ? Math.max(1, Math.round(v)) : 1,
                  )
                }
                max={100000}
                wide
              />
            </div>
          )}
          {engineOn && alloc.isFetching && (
            <span className="flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <RefreshCw className="size-3 animate-spin" /> считаю
              распределение…
            </span>
          )}
          {engineOn && alloc.data && (
            <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span>
                кандидатов{" "}
                <b className="text-foreground">{alloc.data.candidates}</b>
              </span>
              <span>
                без маржи{" "}
                <b className="text-foreground">{alloc.data.margin_missing}</b>
              </span>
              <span>
                на вывод{" "}
                <b className="text-foreground">{alloc.data.delisted}</b>
              </span>
              {(alloc.data.unbridged ?? 0) > 0 && (
                <span title="sku с живым спросом, но без сопоставления с 1С — исключены из движка">
                  без 1С-моста{" "}
                  <b className="text-red-600 dark:text-red-400">
                    {alloc.data.unbridged}
                  </b>
                </span>
              )}
              <span>
                покрытие <b className="text-foreground">{coveragePct}%</b>
              </span>
              <span className="text-muted-foreground/70">
                маржа до {alloc.data.margin_as_of ?? "—"}
              </span>
            </div>
          )}
          {engineOn && alloc.error != null && (
            <span className="ml-auto text-xs text-destructive">
              {extractErrorMessage(alloc.error, "движок недоступен")}
            </span>
          )}
        </div>
      )}

      {/* Товары, у которых «Доупак» не число: вне флаконной модели (норма) и со сломанной спекой
          (дефект данных 1С). Раньше и те и другие молча показывали 0 — неотличимо от «кончилось». */}
      {!loading && hasCap && SUPPLY_UI.assembly && (
        <>
          <BlockedGroup
            items={blocked.spec_problem}
            danger
            summary="Спецификация не разворачивается"
            tail="доупак посчитать нельзя, это дефект данных 1С"
            note="Нет спецификации МП, цикл в составе или компонент без категории. Пока не починено в 1С, «Доупак» по этим товарам не число, а пробел — не путать с «нечего доупаковывать»."
          />
          <BlockedGroup
            items={blocked.no_base}
            summary="Вне флаконной модели"
            tail="доупаковать нельзя, доступно только «Готово»"
            note="У этих товаров спецификация не разворачивается в базовый флакон (саше, декоративка, сторонние товары). Это не ошибка расчёта — но и «произвести флакон» к ним неприменимо."
          />
        </>
      )}

      {/* Нет снимка остатков 1С → «Доупак» не считается. Красный баннер + кнопка (автозапрос выше). */}
      {!loading && hasCap && SUPPLY_UI.assembly && !assembleComputed && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <span className="font-medium">Остатки 1С не сняты</span>
          <span className="text-red-600/90 dark:text-red-300/80">
            — колонка «Доупак» не посчитана (можно собрать из компонентов).
            {snapMut.isPending ? " Снимаю…" : ""}
          </span>
          <button
            type="button"
            onClick={() => snapMut.mutate()}
            disabled={snapMut.isPending}
            className="ml-auto flex items-center gap-1.5 rounded-md border border-red-400 px-2.5 py-1 text-xs font-medium hover:bg-red-100 disabled:opacity-50 dark:hover:bg-red-900/40"
          >
            <RefreshCw
              className={cn("size-3.5", snapMut.isPending && "animate-spin")}
            />{" "}
            Снять остатки 1С
          </button>
        </div>
      )}

      {!loading && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-1 text-xs text-muted-foreground">
          <span className="text-muted-foreground/60">Снимки данных:</span>
          <span className="flex items-center gap-1.5">
            <SnapItem label="Остатки 1С" at={cap.data?.availability_as_of} />
            <button
              type="button"
              onClick={() => snapMut.mutate()}
              disabled={snapMut.isPending}
              title="Снять остатки 1С заново. По этому снимку считается бронь пула при фиксации, а при включённой доупаковке — ещё и «Доупак»"
              className="rounded p-0.5 hover:bg-accent disabled:opacity-50"
            >
              <RefreshCw
                className={cn("size-3", snapMut.isPending && "animate-spin")}
              />
            </button>
          </span>
          <SnapItem label="Кластеры Ozon" at={oz.data?.clusters_fetched_at} />
          <SnapItem label="Поставки WB" at={wbData?.supply_snapshot_at} />
          <SnapItem label="Поставки Ozon" at={oz.data?.supply_snapshot_at} />
          {oz.data?.paid_storage && (
            <PaidStorageDates dates={oz.data.paid_storage.dates} />
          )}
        </div>
      )}

      {!loading && dataWarnings.length > 0 && (
        <div className="space-y-0.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <div className="flex items-center gap-2">
            <span className="font-medium">
              Предупреждения данных ({dataWarnings.length}):
            </span>
            {unmatchedItems.length > 0 && (
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className="ml-auto rounded-md border border-amber-400 px-2 py-0.5 text-[11px] font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40"
              >
                Сопоставить регионы ({unmatchedItems.length})
              </button>
            )}
          </div>
          {dataWarnings.map((w, i) => (
            <div key={i}>• {w}</div>
          ))}
        </div>
      )}
      {mapOpen && (
        <RegionMapModal
          items={unmatchedItems}
          onClose={() => setMapOpen(false)}
        />
      )}

      {!loading && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
          <span className="text-muted-foreground/60">Подсветка:</span>
          <span>
            <span className="font-medium text-red-600 dark:text-red-400">
              глубина
            </span>{" "}
            — риск пустоты (&lt; {floorDays} дн)
          </span>
          <span>
            <span className="text-amber-600 dark:text-amber-400">глубина</span>{" "}
            — перетарка (&gt; {params.overstock} дн)
          </span>
          {engineOn && (
            <span>
              <span className="rounded bg-red-100 px-1 text-red-700 dark:bg-red-950 dark:text-red-300">
                убыток
              </span>{" "}
              — товар в минус по марже
            </span>
          )}
          {engineOn && (
            <>
              <span>
                <span className="font-medium text-blue-500">✎</span> Решение —
                сохранено вручную
              </span>
              <span>
                <span className="font-medium text-orange-500">✎!</span> —
                сохранённое расходится с движком (устарело)
              </span>
            </>
          )}
        </div>
      )}

      {/* Скрытое у себя — ПОСТОЯННАЯ строка, а не тихий фильтр: иначе «у меня этих регионов нет»
          превращается в «данные потерялись», и разбираться приходится вдвоём. */}
      {!loading && hideCounts.total > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
          <EyeOff className="size-3.5 text-muted-foreground" />
          <span>
            Скрыто у вас: <b>{hiddenWords(hidden)}</b>
          </span>
          <span className="text-muted-foreground">
            — потребность и движок их видят, а фиксация увозит РОВНО показанное: решения по
            скрытым строкам останутся, но в поставки не уедут
          </span>
          <button
            type="button"
            onClick={() => setHidden(NOTHING_HIDDEN)}
            className="ml-1 underline-offset-2 hover:underline"
            title="Вернуть все скрытые у себя строки"
          >
            показать
          </button>
        </div>
      )}

      {!loading && (
        <FilterChips
          table={table}
          extra={[
            ...(selCh.size
              ? [
                  {
                    key: "ch",
                    label: `Каналы: ${selCh.size}`,
                    onClear: () => setSelCh(new Set()),
                  },
                ]
              : []),
            ...(selReg.size
              ? [
                  {
                    key: "reg",
                    label: `Регионы: ${selReg.size}`,
                    onClear: () => setSelReg(new Set()),
                  },
                ]
              : []),
            ...(selLegal.size
              ? [
                  {
                    key: "legal",
                    label: `Юрлицо: ${[...selLegal].join(", ") || "не определилось"}`,
                    onClear: () => setSelLegal(new Set()),
                  },
                ]
              : []),
            ...(search.trim()
              ? [
                  {
                    key: "q",
                    label: `Поиск: «${search.trim()}»`,
                    onClear: () => setSearch(""),
                  },
                ]
              : []),
          ]}
        />
      )}

      {!loading && (
        <div ref={gridRef} style={vars} className="rounded-md border">
          {/* заголовок: клик по подписи — сортировка (Shift — добавить), воронка — фильтр колонки */}
          {table.getHeaderGroups().map((hg) => (
            <div
              key={hg.id}
              className="grid border-b bg-muted/50 text-xs font-medium text-muted-foreground"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {hg.headers.map((h, i) => (
                <HeaderCell
                  key={h.id}
                  column={h.column}
                  last={i === hg.headers.length - 1}
                  onResize={(e) => startResize(h.column.id, e)}
                />
              ))}
            </div>
          ))}

          {/* ИТОГО — по ОТФИЛЬТРОВАННЫМ товарам, поэтому строка всегда согласована с тем, что видно */}
          <div
            className="grid border-b bg-muted/60 text-xs font-semibold"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {visibleCols.map((c) => (
              <Cell key={c.id} text={metaOf(c).left}>
                {totalCells[c.id] ?? null}
              </Cell>
            ))}
          </div>

          <TableBody rows={rows} gridTemplate={gridTemplate} />
          {rows.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Ничего не найдено по фильтрам.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Компактное числовое поле: подпись + узкий инпут в строку. Пусто → 0.
function MiniNum({
  label,
  value,
  onChange,
  max = 1000,
  wide = false,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
  wide?: boolean;
  hint?: React.ReactNode;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {hint && <Hint>{hint}</Hint>}
      <input
        type="number"
        min={1}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        // колесо над сфокусированным number-полем молча меняет число, а тут это перезапускает
        // расчёт спроса на минуту и запоминается за пользователем — слишком дорого для случайного
        // движения мышью при прокрутке страницы
        onWheel={(e) => e.currentTarget.blur()}
        className={cn(
          "h-7 rounded border border-input bg-background px-1.5 text-right text-sm tabular-nums focus:border-primary focus:outline-none",
          wide ? "w-20" : "w-14",
        )}
      />
    </label>
  );
}

/** Компактное меню панели: «Группировка: Товар → МП → Регион ⌄». Ряды кнопок-таблеток съедали
 *  пол-панели, а жмут их редко. Закрытие отдаём наружу колбэком: у выбора режима закрываться
 *  сразу — правильно, а вот меню колонок остаётся открытым (там отмечают несколько подряд). */
function ToolbarMenu({
  label,
  value,
  title,
  width = "w-56",
  children,
}: {
  label: string;
  value?: string;
  title?: string;
  width?: string;
  children: (close: () => void) => React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" title={title}>
          <span className="text-muted-foreground">{label}</span>
          {value && <span className="font-medium">{value}</span>}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={cn(width, "grid gap-0.5 p-1")}>
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-2 py-1 text-left text-xs",
        active ? "bg-primary text-primary-foreground" : "hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

// Один снимок данных: «Метка: 3 ч назад · 04.07.2026 09:12» (относительное время + точная дата).
/** Дата данных платного хранения Ozon по каждому юрлицу. Устаревшие подсвечены: плашки по их
 *  товарам считаются по старому дню (предупреждение — в блоке ниже, его кладёт бэк). */
function PaidStorageDates({
  dates,
}: {
  dates: api.OzonPaidStorageMeta["dates"];
}): React.ReactElement {
  return (
    <span
      className="flex items-center gap-1"
      title="Платное хранение Ozon (DWH ozon_paidstorage_reports): последняя дата по каждому юрлицу. Справка на строках кластеров — на потребность не влияет."
    >
      <span className="text-muted-foreground/70">Платное хранение Ozon:</span>
      {dates.map((d, i) => (
        <span
          key={d.legal_name}
          className={cn(
            d.stale
              ? "font-medium text-amber-700 dark:text-amber-400"
              : "text-foreground/80",
          )}
        >
          {d.legal_name} {d.date ? fmtDate(d.date) : "нет данных"}
          {i < dates.length - 1 ? " ·" : ""}
        </span>
      ))}
    </span>
  );
}

function SnapItem({
  label,
  at,
}: {
  label: string;
  at?: string | null;
}): React.ReactElement {
  const dt = at ? fmtDateTime(at) : "";
  return (
    <span className="flex items-center gap-1">
      <span className="text-muted-foreground/70">{label}:</span>
      {at ? (
        <span className="text-foreground/80" title={dt}>
          <span className="font-medium">{timeAgo(at)}</span>
          <span className="text-muted-foreground/60"> · {dt}</span>
        </span>
      ) : (
        <span className="text-muted-foreground/50">нет</span>
      )}
    </span>
  );
}

interface UnmatchedItem {
  mp_code: string;
  mp_label: string;
  source_type: string; // okrug | warehouse
  source_value: string;
  regions: string[];
}

// Модалка сопоставления несопоставленных складов/округов WB/Ozon/YM → регион (было на удалённой вкладке).
function RegionMapModal({
  items,
  onClose,
}: {
  items: UnmatchedItem[];
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const key = (it: UnmatchedItem): string =>
    `${it.mp_code}|${it.source_type}|${it.source_value}`;
  const [sel, setSel] = useState<Record<string, string>>({});
  const [done, setDone] = useState<Set<string>>(() => new Set());
  const mut = useMutation({
    mutationFn: api.createRegionMap,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["supply", "analytics"] }),
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Не удалось сохранить сопоставление")),
  });
  const save = (it: UnmatchedItem): void => {
    const region = sel[key(it)];
    if (!region) return;
    mut.mutate(
      {
        mp_code: it.mp_code,
        source_type: it.source_type,
        source_value: it.source_value,
        region,
      },
      { onSuccess: () => setDone((s) => new Set(s).add(key(it))) },
    );
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold">Сопоставление регионов</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Эти склады/округа не сопоставлены и попадают в «Не известен». Выбери
          целевой регион и сохрани — отчёты пересчитаются.
        </p>
        <table className="w-full text-sm">
          <thead className="border-b text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Канал</th>
              <th className="px-2 py-1.5 text-left font-medium">Источник</th>
              <th className="px-2 py-1.5 text-left font-medium">Тип</th>
              <th className="px-2 py-1.5 text-left font-medium">Регион</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const k = key(it);
              const isDone = done.has(k);
              return (
                <tr key={k} className="border-b last:border-0">
                  <td className="px-2 py-1.5">{it.mp_label}</td>
                  <td className="px-2 py-1.5">{it.source_value}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {it.source_type === "warehouse" ? "склад" : "округ"}
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      value={sel[k] ?? ""}
                      disabled={isDone}
                      onChange={(e) =>
                        setSel((s) => ({ ...s, [k]: e.target.value }))
                      }
                      className="h-7 w-full rounded border bg-background px-1 text-sm disabled:opacity-50"
                    >
                      <option value="">— выбрать —</option>
                      {it.regions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {isDone ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        ✓ сохранено
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => save(it)}
                        disabled={!sel[k] || mut.isPending}
                        className="rounded-md border px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-40"
                      >
                        Сохранить
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.every((it) => done.has(key(it))) && (
          <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">
            Все сопоставлены. Закрой окно — предупреждение уйдёт после
            пересчёта.
          </p>
        )}
      </div>
    </div>
  );
}

// «Почему?» по региону: локальный расчёт + КОНКУРЕНЦИЯ за общий пул товара (кто бился и почему этот).
function WhyPopover({
  line,
  siblings,
  children,
}: {
  line: api.AllocationLine;
  siblings: api.AllocationLine[];
  children: React.ReactNode;
}): React.ReactElement {
  const t = line.trace;
  const num = (v: number | null | undefined): string =>
    v === null || v === undefined ? "—" : int0(v);
  const money = (v: number | null | undefined): string =>
    v === null || v === undefined ? "нет данных" : `${dec1(v)} ₽`;

  // конкуренция за пул товара (все кандидаты этого nom_key)
  const poolReady = traceNum(t, "пул_готово_всего") ?? 0;
  // ⚠️ null = «доупак не считался» (режим ready_only), а не 0: для СУММЫ пула это без разницы
  // (?? 0), но на экране «—» и «0 доупак» — разные ответы, и печатать их разным.
  const poolAssembleRaw = traceNum(t, "пул_доупак_всего");
  const poolAssemble = poolAssembleRaw ?? 0;
  const pool = poolReady + poolAssemble;
  const poolMissingReason = traceStr(t, "пул_причина") ?? "";
  const poolAllPacks = traceNum(t, "пул_готово_всех_упаковок");
  const poolOtherPacks =
    poolAllPacks !== null && poolAllPacks > poolReady
      ? poolAllPacks - poolReady
      : null;
  const chMargin = new Map<string, number | null>();
  for (const s of siblings)
    if (!chMargin.has(s.channel))
      chMargin.set(s.channel, traceNum(s.trace, "маржа_на_штуку"));
  // сентинел «нет маржи» = -Infinity (НЕ -1: -1 сталкивается с реальной отрицательной маржой убыточных)
  const bestMargin = Math.max(
    ...[...chMargin.values()].map((v) => v ?? -Infinity),
  );
  const chList = [...chMargin.entries()].sort(
    (a, b) => (b[1] ?? -Infinity) - (a[1] ?? -Infinity),
  );
  const ranked = [...siblings].sort(
    (a, b) =>
      b.priority - a.priority ||
      (traceNum(b.trace, "срочно_штук") ?? 0) -
        (traceNum(a.trace, "срочно_штук") ?? 0) ||
      b.need - a.need,
  );
  const myRank =
    ranked.findIndex(
      (s) => s.channel === line.channel && s.region === line.region,
    ) + 1;
  const served = siblings
    .filter((s) => s.allocated > 0)
    .sort((a, b) => b.allocated - a.allocated);
  const myMargin = traceNum(t, "маржа_на_штуку") ?? -Infinity;
  const lostReason =
    line.allocated > 0
      ? null
      : line.reason === "no_margin_residual"
        ? "остаточный канал (без маржи)"
        : myMargin < bestMargin
          ? "канал проиграл по марже"
          : `пул кончился раньше (ранг #${myRank})`;

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-96 text-xs">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 border-b pb-1.5">
            <span className="text-sm font-semibold">
              Почему {int0(line.allocated)} из {int0(line.need)}?
            </span>
            <span className={cn("font-medium", REASON_TEXT[line.reason])}>
              {REASON_LABEL[line.reason] ?? line.reason}
            </span>
          </div>

          {/* локальный расчёт клетки */}
          <div className="space-y-1">
            <WhyRow
              k="Приоритет (прибыль/очко)"
              v={`${dec1(line.priority)} ₽`}
              strong
            />
            <div className="rounded bg-muted/40 px-2 py-1 font-mono text-[11px] text-muted-foreground">
              маржа {money(traceNum(t, "маржа_на_штуку"))} ÷{" "}
              {num(traceNum(t, "очков_на_штуку"))} очк = {dec1(line.priority)}{" "}
              ₽/очко
            </div>
            <WhyRow
              k="Срочно (защита), шт"
              v={num(traceNum(t, "срочно_штук"))}
            />
          </div>

          {/* конкуренция за общий пул товара */}
          <div className="space-y-1.5 border-t pt-1.5">
            <div className="font-medium text-foreground">
              Конкуренция за пул товара
            </div>
            {poolMissingReason && (
              <WhyRow k="Почему нет пула" v={poolMissingReason} />
            )}
            <WhyRow
              k="Пул: готово + доупак"
              v={`${num(poolReady)} + ${num(poolAssembleRaw)} = ${int0(pool)} на ${siblings.length}`}
            />
            {poolOtherPacks !== null && (
              <WhyRow
                k="В других упаковках (не годится)"
                v={int0(poolOtherPacks)}
              />
            )}
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-muted-foreground">Канал по марже:</span>
              {chList.map(([ch, m]) => (
                <span
                  key={ch}
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[11px]",
                    (m ?? -Infinity) === bestMargin && m !== null
                      ? "bg-emerald-100 font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {CH_LABEL[ch as Channel] ?? ch}{" "}
                  {m === null ? "—" : `${dec1(m)}₽`}
                  {(m ?? -Infinity) === bestMargin && m !== null ? " ✓" : ""}
                </span>
              ))}
            </div>
            <WhyRow
              k="Твой ранг (срочность ↓)"
              v={`#${myRank} из ${siblings.length}`}
            />
            <WhyRow
              k="Осталось пула к твоей очереди"
              v={num(traceNum(t, "пул_остаток_на_момент"))}
            />
            {served.length > 0 && (
              <div className="leading-relaxed">
                <span className="text-muted-foreground">Получили: </span>
                <span className="text-foreground">
                  {served
                    .slice(0, 5)
                    .map((s) => `${s.region} ${int0(s.allocated)}`)
                    .join(" · ")}
                  {served.length > 5 ? " …" : ""}
                </span>
              </div>
            )}
            {line.shares_flacon_with.length > 0 && (
              <div className="leading-relaxed border-t pt-1.5">
                <span className="text-muted-foreground">
                  Общий флакон делит с:{" "}
                </span>
                <span className="text-foreground">
                  {line.shares_flacon_with.join(" · ")}
                </span>
              </div>
            )}
            {lostReason && (
              <div className={cn("font-medium", REASON_TEXT[line.reason])}>
                Проиграл: {lostReason}
              </div>
            )}
          </div>

          <div className="border-t pt-1.5">
            <WhyRow k="Итог: дали" v={num(traceNum(t, "дали"))} strong />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function WhyRow({
  k,
  v,
  strong,
}: {
  k: string;
  v: string;
  strong?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span
        className={cn(
          "tabular-nums",
          strong && "font-semibold text-foreground",
        )}
      >
        {v}
      </span>
    </div>
  );
}
